import { useState, useEffect, useRef } from 'react'
import { Upload, FileText, Check, RefreshCw, X, Download, Eye, Loader2, Plus, Archive } from 'lucide-react'
import { formatDate } from '../../utils/helpers'
import api from '../../services/api'
import DraftBar from '../common/DraftBar.jsx'
import shrinkImage from '../../utils/shrinkImage'
import { useNavigate } from 'react-router-dom'
import { useAuth, userRoles } from '../../hooks/useAuth'
import InvoiceView from './InvoiceView.jsx'
import MilestoneChat from '../chat/MilestoneChat.jsx'
import FileViewerModal from '../common/FileViewerModal.jsx'
import AIDeliverableCheck from '../ai/AIDeliverableCheck.jsx'
import SignaturePad from '../common/SignaturePad.jsx'
import { saveFileDraft, loadFileDraft, deleteFileDraft } from '../../services/draftStore.js'
import { useTranslation } from 'react-i18next'

/**
 * Project Deliverables tab — used by:
 *   • Consultant (assigned to the project): submits new deliverables
 *   • PM / Admin: reviews submissions (approve / request revision / reject)
 *
 * One unified component scoped by role.
 */

const STATUS_STYLES = {
  Submitted: 'bg-blue-100 text-blue-700',
  Approved: 'bg-green-100 text-green-700',
  'Revision Requested': 'bg-amber-100 text-amber-700',
  Rejected: 'bg-red-100 text-red-700',
}

// The public APISIX gateway rejects any request body over ~50 MB with a 413 BEFORE it
// reaches the app (measured: 40 MB passes, 60 MB fails). That 413 has no JSON detail, so
// a too-big upload could only surface as a generic "Failed to submit". The submit flow
// therefore uploads each file in its own small request; this guard is the client-side
// backstop for any SINGLE file that is still too large on its own.
const MAX_UPLOAD_MB = 40
// Bills are photographs of receipts, taken on a phone, several at a time. As taken they can add
// up past the cap; re-encoded they are a fraction of it and just as readable. Shrink before
// judging the size, or a set of perfectly ordinary receipts is refused for being photographs.
const shrinkAll = (files) => Promise.all((files || []).map(shrinkImage))

const oversizeError = (files) => {
  const total = files.reduce((sum, f) => sum + (f?.size || 0), 0)
  if (total > MAX_UPLOAD_MB * 1024 * 1024) {
    const mb = (total / 1024 / 1024).toFixed(0)
    return `A file (or files) totalling ${mb} MB is over the ${MAX_UPLOAD_MB} MB per-upload limit. Please compress it and try again.`
  }
  return null
}

export default function DeliverablesTab({ project, openMilestoneChatId, highlightId, autoConfirmMilestoneId, isArchived = false, onArtifactChange, onProjectDataChange, isCoManager = false, isApprover = false }) {
  const { t: tc } = useTranslation('common')
  const { t: tDel } = useTranslation('deliverables')
  const { user } = useAuth()
  // useNavigate was imported and never called, while two click handlers below called
  // navigate(...) — adding banking details, and confirming an invoice. Both threw
  // "ReferenceError: navigate is not defined" and blanked the page. eslint's no-undef finds
  // this; the build does not, because esbuild never resolves identifiers across scopes.
  const navigate = useNavigate()
  const [deliverables, setDeliverables] = useState([])
  const [milestones, setMilestones] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const [showSubmit, setShowSubmit] = useState(false)
  const [reviewBusy, setReviewBusy] = useState(null)   // decision in-flight → button feedback
  // Over-budget → one-tap "Notify my PM" (chat + email). Holds {milestoneId, total} when
  // a submission was blocked by the milestone's System/Tentative Budget cap.
  const [budgetPmRequest, setBudgetPmRequest] = useState(null)
  const [notifyingPm, setNotifyingPm] = useState(false)
  // Money display: always 2 decimals (e.g. $300.00).
  const usd2 = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  // Deliverable files are multiple; reimbursements are grouped by expense category.
  const EMPTY_REIMB = { Visa: [], Hotels: [], Flights: [], Taxi: [], Daily: [], Other: [] }
  // Expense categories for the reimbursement dropdown (Mam #23): pick a category, attach
  // the bill(s), repeat — each entry lands in the list below with its category tag.
  const REIMB_CATEGORIES = [
    { value: 'Visa', label: 'Visa' },
    { value: 'Hotels', label: 'Hotel' },
    { value: 'Flights', label: 'Tickets / Flights' },
    { value: 'Taxi', label: "Incidentals — Taxi's" },
    { value: 'Daily', label: 'Incidentals — Daily expenses' },
    { value: 'Other', label: 'Incidentals — Other' },
  ]
  const reimbLabel = (v) => (REIMB_CATEGORIES.find(c => c.value === v)?.label || v)
  const [reimbCat, setReimbCat] = useState('Visa')
  const [form, setForm] = useState({ milestone_id: '', title: '', description: '', files: [], reimb: { ...EMPTY_REIMB }, otherAmount: '', otherNote: '' })
  // In-app file preview modal: { url, filename, download }
  const [viewer, setViewer] = useState(null)
  // Reimbursements apply per MILESTONE, not per project: an Actual project's milestones are
  // all reimbursable; a Hybrid project's are reimbursable only when that milestone's mode is
  // Actual; a Fixed project's never are. So the bills section keys off the selected milestone.
  const milestoneIsReimbursable = (msId) => {
    const bt = project?.budget_type || project?.budgetType || 'Fixed'
    if (bt === 'Actual') return true
    if (bt === 'Hybrid') {
      const m = milestones.find(x => String(x.id) === String(msId))
      return (m?.payment_mode || m?.paymentMode) === 'Actual'
    }
    return false
  }
  const isReimbursable = milestoneIsReimbursable(form.milestone_id)
  const isReimbursableProject = ['Actual', 'Hybrid'].includes(project?.budget_type || project?.budgetType)
  const [submitting, setSubmitting] = useState(false)
  const [hasActiveContract, setHasActiveContract] = useState(null) // null = loading, true/false = resolved

  const [reviewingId, setReviewingId] = useState(null)
  const [reviewNotes, setReviewNotes] = useState('')
  const [reviewFile, setReviewFile] = useState(null)   // optional PM revision-notes attachment
  const [batchIds, setBatchIds] = useState([])         // deliverables ticked for batch approve
  const [batching, setBatching] = useState(false)
  const [viewInvoiceMilestoneId, setViewInvoiceMilestoneId] = useState(null)
  // Confirm Invoice modal — replaces the old window.confirm/window.prompt dance with a
  // single review-and-sign step (bank details shown, claimed amount if reimbursable,
  // typed name + optional signature) matching the contract/KYC signing pattern.
  const [confirmInvoiceModal, setConfirmInvoiceModal] = useState(null) // { milestoneId, budgetType, reimbTotal, banking } | null
  const [confirmClaimAmount, setConfirmClaimAmount] = useState('')
  const [confirmSigName, setConfirmSigName] = useState('')
  const [confirmSigData, setConfirmSigData] = useState(null)
  const [confirmSubmitting, setConfirmSubmitting] = useState(false)

  const isConsultant = user?.role === userRoles.CONSULTANT
  // A delegated co-manager acts as a manager on THIS project — they review deliverables and
  // run invoices like the PM. (The backend grants them the same rights via ProjectMember.can_manage.)
  // isApprover is included so a scoped approver can reach the invoice-approval block. Previously
  // this was Admin/PM/co-manager only, so an approver whose global role is Consultant saw NO
  // approve button — and granting them manage to make it appear silently voided their approver
  // rights (self-approval guard) and pushed their approval into the PM slot. Closed dead end.
  const isPMOrAdmin = user?.role === userRoles.ADMIN || user?.role === userRoles.PROJECT_MANAGER || isCoManager || isApprover
  // Who fills WHICH invoice-approval slot: the owning PM / co-manager / Master PM fills the PM
  // slot; the Admin or a delegated scoped approver (a PM from the parent program) fills the ADMIN slot.
  const _managesThisProject = isCoManager || !!user?.is_primary_pm
    || project?.project_manager_id === user?.id || project?.projectManagerId === user?.id
  // A consultant "owns" this project's deliverables view if they're the primary consultant OR
  // they own ANY milestone on it (workshop / offer-to-more copies don't set the project's
  // primary consultant_id, so checking only that wrongly hid Submit Deliverable from them).
  const _ownsAMilestone = isConsultant && (milestones || []).some(
    m => (m.consultant_id ?? m.consultantId) === user?.id
  )
  // Owning ONE milestone must not grant consultant powers over ANOTHER consultant's milestone on
  // the same project — raising/signing an invoice is per-milestone, not per-project.
  const ownsMilestone = (m) => isConsultant && !!m && ((m.consultant_id ?? m.consultantId) === user?.id)
  const isMyProject = isConsultant && (
    project?.consultantId === user.id || project?.consultant_id === user.id || _ownsAMilestone
  )
  // What a deliverable draft keeps. Files are dropped deliberately — see DraftBar's warning,
  // which is shown to the person before they rely on this. Bill CATEGORIES are kept even though
  // the files are not, because "I had a taxi receipt and a hotel bill" is the part that is
  // annoying to reconstruct from memory.
  const collectDeliverableDraft = () => ({
    milestone_id: form.milestone_id,
    title: form.title,
    description: form.description,
    otherAmount: form.otherAmount,
    otherNote: form.otherNote,
    reimbCategories: Object.entries(form.reimb || {})
      .filter(([, v]) => (v || []).length > 0)
      .map(([k]) => k),
  })
  // Put the actual attachments back, if this is the browser that saved them.
  const restoreDraftFiles = async (draftId) => {
    if (!draftId) return
    const local = await loadFileDraft(`deliverable-files-u${user?.id || 'anon'}-${draftId}`)
    if (!local) return
    setForm(f => ({
      ...f,
      files: local.files || [],
      reimb: local.reimb ? { ...EMPTY_REIMB, ...local.reimb } : f.reimb,
    }))
    // The READING of each bill, not just the bill. Without these the receipts come back but the
    // claim total reads $0.00 — the files are there, the money is gone, and the only way to get
    // it back is to remove every bill and re-attach it so the AI reads them again.
    if (local.previews) setBillPreviews(p => ({ ...p, ...local.previews }))
    if (local.edits) setBillEdits(p => ({ ...p, ...local.edits }))
  }

  // Park the attachments against the draft id. Best-effort — if IndexedDB is unavailable the
  // draft still saves, it simply comes back without them.
  const onDeliverableDraftSaved = async (draftId) => {
    const hasFiles = (form.files || []).length > 0 ||
      Object.values(form.reimb || {}).some(a => (a || []).length > 0)
    if (!hasFiles) return
    await saveFileDraft(`deliverable-files-u${user?.id || 'anon'}-${draftId}`, {
      files: form.files, reimb: form.reimb,
      // Saved alongside the bills, always. previews holds what the AI read off each receipt and
      // edits holds any correction the consultant made to it — both are the answer to "how much
      // am I claiming", and a bill without them is just a picture.
      previews: billPreviews, edits: billEdits,
      savedAt: new Date().toISOString(),
    })
  }

  const applyDeliverableDraft = (p, draftId) => {
    setForm(f => ({
      ...f,
      milestone_id: p.milestone_id || '',
      title: p.title || '',
      description: p.description || '',
      otherAmount: p.otherAmount ?? '',
      otherNote: p.otherNote || '',
      // Filled in below from IndexedDB if this browser has them; left empty otherwise, rather
      // than showing a selection the draft cannot actually submit.
      files: [],
      reimb: { ...EMPTY_REIMB },
    }))
    restoreDraftFiles(draftId)
  }

  // An admin submitting FOR a consultant who cannot do it themselves. The consultant is
  // whoever owns the milestone — never a chooser, because the milestone already answers it and
  // a dropdown of every consultant is a way to file somebody's work under the wrong name.
  const actingAsConsultantId = (() => {
    if (isConsultant || !isPMOrAdmin || !form.milestone_id) return null
    const m = (milestones || []).find(x => String(x.id) === String(form.milestone_id))
    return (m?.consultant_id ?? m?.consultantId) || null
  })()
  const actingAsName = (() => {
    if (!actingAsConsultantId) return null
    const m = (milestones || []).find(x => String(x.id) === String(form.milestone_id))
    return m?.consultant_name || m?.consultantName || 'the assigned consultant'
  })()
  // Milestones an admin could submit against: someone must own them, or there is nobody to
  // submit for.
  const submittableForOthers = isPMOrAdmin && !isConsultant && !isArchived
    && (milestones || []).some(m => (m.consultant_id ?? m.consultantId))

  // Only the Admin or the OWNING PM may download from an archived project (team is view-only).
  const canRetrieve = user?.role === userRoles.ADMIN
    || project?.project_manager_id === user?.id || project?.projectManagerId === user?.id
  const canDownload = !isArchived || canRetrieve

  // Per-deliverable archive (PM/Admin, active project, finished deliverable).
  const archiveDeliverable = async (d) => {
    if (!window.confirm(`Archive "${d.title}"? It starts a 10-year retention clock and locks the deliverable from further edits.`)) return
    setError(null)
    try {
      await api.post(`/deliverables/${d.id}/archive`)
      await loadAll()
      onArtifactChange?.()
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not archive the deliverable.')
    }
  }

  // Admin's undo for a mistaken archive. Refused by the server once the whole project is archived.
  const unarchiveDeliverable = async (d) => {
    if (!window.confirm(`Un-archive "${d.title}"? It unlocks for edits and its retention clock is cleared.`)) return
    setError(null)
    try {
      await api.post(`/deliverables/${d.id}/unarchive`)
      await loadAll()
      onArtifactChange?.()
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not un-archive the deliverable.')
    }
  }

  // Deep-link highlight: scroll to and briefly pulse the milestone/deliverable named by ?highlight=
  const [highlighted, setHighlighted] = useState(null)
  useEffect(() => {
    if (!highlightId || loading) return
    const el = document.getElementById(`ms-${highlightId}`) || document.getElementById(`dlv-${highlightId}`)
    if (!el) return
    el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    setHighlighted(String(highlightId))
    const t = setTimeout(() => setHighlighted(null), 2600)
    return () => clearTimeout(t)
  }, [highlightId, loading, milestones])

  const loadAll = async () => {
    if (!project?.id) return
    setLoading(true)
    setError(null)
    try {
      const [delivRes, msRes, contractsRes] = await Promise.all([
        api.get(`/deliverables/project/${project.id}`),
        api.get(`/projects/${project.id}/milestones/`),
        api.get(`/contracts/`).catch(() => ({ data: [] })),
      ])
      // Is there an Active contract that unlocks deliverables? For a CONSULTANT, gate on THEIR
      // OWN contract (on a multi-consultant project, another consultant's active contract must
      // not unlock/lock this one). For PM/Admin, any active contract on the project counts.
      const activeContracts = (contractsRes.data || []).filter(
        c => c.project_id === project.id && c.status === 'Active'
      )
      const relevant = isConsultant
        ? activeContracts.filter(c => (c.consultant_id ?? c.consultantId) === user?.id)
        : activeContracts
      setHasActiveContract(relevant.length > 0)
      setDeliverables(delivRes.data || [])
      setMilestones(msRes.data || [])
      // Deliverable/invoice actions change project.progress, spent_budget, remaining_budget,
      // and status — tell the parent to refresh so the Overview/Budget tabs and lifecycle
      // map (which read from ProjectDetails' own project/milestones state) don't go stale
      // after an approval/confirm/payment on THIS same page.
      if (onProjectDataChange) onProjectDataChange()
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load deliverables')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { loadAll() }, [project?.id])

  // Auto-reopen the Confirm & Sign Invoice dialog after the consultant updated banking from it
  // (?confirmInvoice=<id>). Fires once, after data has loaded and the milestone is present.
  const autoConfirmDone = useRef(false)
  useEffect(() => {
    if (!autoConfirmMilestoneId || loading || autoConfirmDone.current) return
    const ms = milestones.find(m => String(m.id) === String(autoConfirmMilestoneId))
    if (!ms) return
    autoConfirmDone.current = true
    confirmInvoice(ms.id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoConfirmMilestoneId, loading, milestones])

  // ── Submit (Consultant) ────────────────────────────────────────────────
  // Text-only draft: persist the typed fields per milestone so a closed/reopened Submit modal
  // restores them. Files can't be persisted in the browser (a File can't be put back into a
  // file input), so they must be re-attached — the draft note says so.
  // WHOSE draft. Without the user id, one browser meant one draft: sign in as the consultant,
  // stage a deliverable and some bills, sign in as the Admin, and the Admin's "Submit for the
  // consultant" screen opened holding the consultant's files and amounts — announced as "Draft
  // restored", for a draft they never saved. On a shared or office machine that is somebody
  // else's work sitting in your form, one click from being submitted under your name.
  //
  // IndexedDB and localStorage are per-BROWSER, not per-person. Anything stored in them has to
  // carry the user id or it belongs to whoever logs in next.
  const draftKey = (mid) => `deliverable-draft-u${user?.id || 'anon'}-${project?.id}-${mid}`
  const loadDraft = (mid) => {
    try { return JSON.parse(localStorage.getItem(draftKey(mid)) || 'null') } catch { return null }
  }
  const clearDraft = (mid) => { try { localStorage.removeItem(draftKey(mid)) } catch { /* ignore */ } }

  const openSubmit = () => {
    const mid = milestones[0]?.id || ''
    const d = mid ? loadDraft(mid) : null
    setForm({
      milestone_id: mid,
      title: d?.title || '', description: d?.description || '',
      files: [], reimb: { ...EMPTY_REIMB },
      otherAmount: d?.otherAmount || '', otherNote: d?.otherNote || '',
    })
    setEditingDeliverable(null)
    setShowSubmit(true)
    if (mid) hydrateFromDraft(mid)   // restore attached files + amounts saved as a draft
  }

  // When the consultant switches the milestone in the modal, load that milestone's draft.
  // Files belong to a milestone, so reset them first, then restore that milestone's draft.
  const switchSubmitMilestone = (mid) => {
    const d = loadDraft(mid)
    setForm(f => ({ ...f, milestone_id: mid, title: d?.title || '', description: d?.description || '', otherAmount: d?.otherAmount || '', otherNote: d?.otherNote || '', files: [], reimb: { ...EMPTY_REIMB } }))
    setBillPreviews({}); setBillEdits({})
    hydrateFromDraft(mid)
  }


  // Pre-submit extraction preview: as soon as a bill is attached, the AI reads it and the
  // exact amount shows next to the chip — so the consultant verifies BEFORE submitting.
  const [billPreviews, setBillPreviews] = useState({})   // "name::size" -> {status, amount_usd, currency, amount, date, rate_date, vendor, description}
  // Consultant corrections on the Verify screen, keyed the same way. Overrides the AI preview.
  const [billEdits, setBillEdits] = useState({})          // key -> {amount, currency, date, amount_usd, rate_date}
  const billKey = (f) => `${f.name}::${f.size}`
  const [draftRestored, setDraftRestored] = useState(false)

  // Restore a saved file-draft (attached files + bill amounts) for a milestone, if one exists.
  // Best-effort and additive: on any miss/failure the modal just opens empty as before.
  const hydrateFromDraft = async (mid) => {
    const d = await loadFileDraft(draftKey(mid))
    if (!d) { setDraftRestored(false); return }
    setForm(f => ({
      ...f,
      milestone_id: mid,
      title: d.title ?? f.title,
      description: d.description ?? f.description,
      otherAmount: d.otherAmount ?? f.otherAmount,
      otherNote: d.otherNote ?? f.otherNote,
      files: Array.isArray(d.files) ? d.files : f.files,
      reimb: d.reimb ? { ...EMPTY_REIMB, ...d.reimb } : f.reimb,
    }))
    if (d.previews) setBillPreviews(p => ({ ...p, ...d.previews }))
    if (d.edits) setBillEdits(p => ({ ...p, ...d.edits }))
    setDraftRestored(true)
  }

  // Closing the submit modal: offer to keep everything (files + amounts) as a draft for this
  // milestone, so a half-finished submission (e.g. still waiting on one bill) isn't lost.
  // Edit mode is unaffected (it edits an existing deliverable, not a draft).
  const closeSubmit = async () => {
    if (!editingDeliverable && form.milestone_id) {
      const hasFiles = form.files.length > 0 || Object.values(form.reimb).some(a => (a || []).length > 0)
      const hasText = !!(form.title || form.description || form.otherAmount || form.otherNote)
      if (hasFiles || hasText) {
        const keep = window.confirm('Save your progress as a draft for this milestone?\n\nYour attached files and amounts will be kept so you can come back and finish (e.g. once you have the last bill).')
        if (keep) {
          await saveFileDraft(draftKey(form.milestone_id), {
            title: form.title, description: form.description,
            otherAmount: form.otherAmount, otherNote: form.otherNote,
            files: form.files, reimb: form.reimb,
            previews: billPreviews, edits: billEdits,
            savedAt: new Date().toISOString(),
          })
        } else {
          clearDraft(form.milestone_id)
          await deleteFileDraft(draftKey(form.milestone_id))
        }
      }
    }
    setShowSubmit(false); setEditingDeliverable(null)
    setBillPreviews({}); setBillEdits({}); setDraftRestored(false)
  }
  const previewBill = async (f) => {
    const key = billKey(f)
    setBillPreviews(prev => ({ ...prev, [key]: { status: 'loading' } }))
    try {
      const fd = new FormData()
      fd.append('file', f)
      const r = await api.post('/deliverables/parse-bill', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      setBillPreviews(prev => ({ ...prev, [key]: { status: 'done', ...r.data } }))
    } catch {
      setBillPreviews(prev => ({ ...prev, [key]: { status: 'failed' } }))
    }
  }
  // The effective values for a bill = AI preview with any consultant edits layered on top.
  const effBill = (f) => {
    const pv = billPreviews[billKey(f)] || {}
    const ed = billEdits[billKey(f)] || {}
    return {
      status: pv.status,
      amount: ed.amount !== undefined ? ed.amount : (pv.amount ?? null),
      currency: ed.currency !== undefined ? ed.currency : (pv.currency ?? null),
      date: ed.date !== undefined ? ed.date : (pv.date ?? null),
      amount_usd: ed.amount_usd !== undefined ? ed.amount_usd : (pv.amount_usd ?? null),
      rate_date: ed.rate_date !== undefined ? ed.rate_date : (pv.rate_date ?? null),
      vendor: pv.vendor ?? null,
      note: pv.description ?? null,
    }
  }
  // Edit a bill's amount/currency/date on the Verify screen; re-convert USD at that date's rate.
  const updateBill = async (f, patch) => {
    const key = billKey(f)
    setBillEdits(prev => ({ ...prev, [key]: { ...prev[key], ...patch } }))
    const cur = effBill(f)
    const amount = patch.amount !== undefined ? patch.amount : cur.amount
    const currency = patch.currency !== undefined ? patch.currency : cur.currency
    const date = patch.date !== undefined ? patch.date : cur.date
    const amt = parseFloat(amount)
    if (isNaN(amt) || !currency) return
    try {
      const r = await api.get('/deliverables/fx-convert', { params: { amount: amt, currency, date: date || '' } })
      setBillEdits(prev => ({ ...prev, [key]: { ...prev[key], amount_usd: r.data?.usd ?? null, rate_date: r.data?.rate_date ?? null } }))
    } catch { /* keep prior USD */ }
  }

  // Add/remove files within a category or the deliverable file list (allows cancel/reattach).
  const addFilesTo = (bucket, fileList) => {
    const incoming = Array.from(fileList || [])
    if (!incoming.length) return
    setForm(prev => bucket === 'files'
      ? { ...prev, files: [...prev.files, ...incoming] }
      : { ...prev, reimb: { ...prev.reimb, [bucket]: [...(prev.reimb[bucket] || []), ...incoming] } })
    if (bucket !== 'files') incoming.forEach(previewBill)
  }
  const removeFileAt = (bucket, idx) => {
    setForm(prev => bucket === 'files'
      ? { ...prev, files: prev.files.filter((_, i) => i !== idx) }
      : { ...prev, reimb: { ...prev.reimb, [bucket]: prev.reimb[bucket].filter((_, i) => i !== idx) } })
  }

  // Drag-and-drop support: a consultant can drop files onto the upload zones instead of
  // clicking. Returns the DnD handlers for a bucket ('files' or a reimbursement category).
  const [dragBucket, setDragBucket] = useState(null)
  const dropZone = (bucket) => ({
    onDragOver: (e) => { e.preventDefault() },
    onDragEnter: (e) => { e.preventDefault(); setDragBucket(bucket) },
    onDragLeave: (e) => { e.preventDefault(); if (e.currentTarget === e.target) setDragBucket(null) },
    onDrop: (e) => { e.preventDefault(); setDragBucket(null); addFilesTo(bucket, e.dataTransfer.files) },
  })

  // Verify & Submit gate: when reimbursement claims are attached, the consultant first sees a
  // table of every bill's extracted amount (+ live USD conversion) and must CONFIRM before the
  // deliverable actually submits — the claim figures are locked in knowingly, never blind.
  const [verifyOpen, setVerifyOpen] = useState(false)
  const buildVerifyRows = () => {
    const rows = []
    if (editingDeliverable) {
      editBills.forEach(rf => rows.push({
        key: `e${rf.id}`, cat: rf.category || '—',
        name: rf.original_filename || 'Other incidental',
        orig: (rf.currency && rf.currency !== 'USD' && rf.amount_original != null) ? `${rf.currency} ${usd2(rf.amount_original)}` : null,
        usd: rf.amount != null ? Number(rf.amount) : null,
        status: 'done',
      }))
    }
    Object.entries(form.reimb).forEach(([cat, files]) => files.forEach((f, i) => {
      const b = effBill(f)
      rows.push({
        key: `n${cat}${i}`, cat,
        name: f.name,
        file: f, editable: true,
        amount: b.amount, currency: b.currency, date: b.date,
        rateDate: b.rate_date,
        usd: b.amount_usd != null ? Number(b.amount_usd) : null,
        status: b.status || 'loading',
      })
    }))
    const other = parseFloat(form.otherAmount)
    if (!isNaN(other) && other > 0) {
      rows.push({ key: 'other', cat: 'Other', name: form.otherNote || 'Other incidental (typed amount)', orig: null, usd: other, status: 'done' })
    }
    return rows
  }

  const handleFormSubmit = (e) => {
    e.preventDefault()
    if (editingDeliverable) {
      if (!form.title.trim()) { setError('Title is required.'); return }
    } else if (!form.milestone_id || !form.title.trim() || form.files.length === 0) {
      setError('Milestone, title and at least one file are required.')
      return
    }
    // An incidental amount with no explanation leaves the PM/Finance guessing — require a note.
    if (milestoneIsReimbursable(form.milestone_id) && parseFloat(form.otherAmount) > 0 && !form.otherNote.trim()) {
      setError('Please add a note explaining what the "Incidentals — Other" amount was for.')
      return
    }
    // Claims attached → verify table first; otherwise submit straight away.
    const hasClaims = milestoneIsReimbursable(form.milestone_id) && (
      Object.values(form.reimb).some(files => files.length > 0) ||
      (parseFloat(form.otherAmount) > 0) ||
      (editingDeliverable && editBills.length > 0)
    )
    if (hasClaims) { setVerifyOpen(true); return }
    return editingDeliverable ? saveEdit() : doSubmit()
  }

  // Pull the "$1,234.56" figure out of a backend budget message so we can pass it to the PM.
  const parseMoney = (msg) => {
    const m = /\$([\d,]+(?:\.\d+)?)/.exec(msg || '')
    return m ? parseFloat(m[1].replace(/,/g, '')) : null
  }
  // One-tap: notify THIS milestone's PM (chat DM + email + in-app) that the budget is exceeded.
  const notifyPmBudget = async () => {
    if (!budgetPmRequest) return
    setNotifyingPm(true)
    try {
      const res = await api.post(
        `/deliverables/milestones/${budgetPmRequest.milestoneId}/request-budget-increase`,
        { attempted_total: budgetPmRequest.total },
      )
      setBudgetPmRequest(null)
      setError(null)
      alert(res.data?.detail || 'Your PM has been notified.')
    } catch (e) {
      alert(e.response?.data?.detail || 'Could not notify your PM. Please try again.')
    } finally {
      setNotifyingPm(false)
    }
  }

  const doSubmit = async () => {
    // Uploads are SPLIT into small requests: the API gateway rejects any single request
    // body over ~50 MB with a 413 before it reaches the app. The old code bundled the
    // deliverable file + every bill into ONE request, which overflowed that cap and failed
    // as a silent "Failed to submit". Now we create the deliverable first, then send each
    // bill in its own small request — none can approach the cap. Guard the one remaining
    // multi-file request (deliverable + extra content files) client-side.
    const files = await shrinkAll(form.files)
    const tooBig = oversizeError(files)
    if (tooBig) { setError(tooBig); return }
    setSubmitting(true)
    setError(null)
    try {
      // 1. Create the deliverable: main file + extra content files + text + the typed
      //    incidental-other amount (no bill FILES here — those go one at a time below).
      const fd = new FormData()
      fd.append('milestone_id', form.milestone_id)
      fd.append('title', form.title)
      if (form.description) fd.append('description', form.description)
      // First file is the primary deliverable; the rest are extra content files.
      fd.append('file', files[0])
      files.slice(1).forEach(f => fd.append('extra_files', f))
      if (milestoneIsReimbursable(form.milestone_id) && form.otherAmount !== '' && !isNaN(parseFloat(form.otherAmount))) {
        fd.append('incidental_other_amount', parseFloat(form.otherAmount))
        if (form.otherNote) fd.append('incidental_other_note', form.otherNote)
      }
      // Present only when an admin is submitting for the milestone's owner. The backend
      // records the work as the consultant's and the action as the admin's.
      if (actingAsConsultantId) fd.append('on_behalf_of', actingAsConsultantId)
      const res = await api.post('/deliverables/', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      const deliverableId = res.data?.id

      // 2. Upload each reimbursement bill as its OWN small request (never for a Fixed
      //    milestone even if bills were attached before switching selection).
      const failed = []
      let budgetErr = null   // set if a bill upload is rejected for exceeding the milestone budget
      if (milestoneIsReimbursable(form.milestone_id) && deliverableId) {
        const bills = []
        Object.entries(form.reimb).forEach(([cat, files]) => (files || []).forEach(f => bills.push({ cat, f })))
        for (const { cat, f } of bills) {
          if (oversizeError([f])) { failed.push(f.name); continue }
          try {
            const bfd = new FormData()
            bfd.append('reimbursement_files', f)
            bfd.append('reimbursement_categories', cat)
            // Send the consultant-VERIFIED amount/currency/date so the stored USD matches
            // exactly what they confirmed (converted at the bill's date).
            const b = effBill(f)
            if (b.amount != null && b.amount !== '') bfd.append('reimbursement_amounts', b.amount)
            if (b.currency) bfd.append('reimbursement_currencies', b.currency)
            if (b.date) bfd.append('reimbursement_dates', b.date)
            if (b.note) bfd.append('reimbursement_notes', b.note)
            if (b.vendor) bfd.append('reimbursement_vendors', b.vendor)
            await api.post(`/deliverables/${deliverableId}/bills`, bfd, { headers: { 'Content-Type': 'multipart/form-data' } })
          } catch (be) {
            // A 422 here means the milestone's System/Tentative Budget would be exceeded — a
            // clear "contact your PM" case, NOT a file-upload failure. Stop and surface it.
            if (be.response?.status === 422) { budgetErr = be.response.data?.detail || 'This milestone budget would be exceeded.'; break }
            failed.push(f.name)
          }
        }
      }

      if (budgetErr) {
        // Roll the whole submission back so nothing partial remains (the backend already
        // rejected the over-budget bills; the deliverable itself is still here). Keep the
        // draft so the consultant can resubmit once the PM raises the budget.
        try { await api.delete(`/deliverables/${deliverableId}`) } catch { /* best-effort */ }
        setVerifyOpen(false); setShowSubmit(false)
        await loadAll()
        setError(budgetErr)
        setBudgetPmRequest({ milestoneId: Number(form.milestone_id), total: parseMoney(budgetErr) })
        setSubmitting(false)
        return
      }

      clearDraft(form.milestone_id)   // submitted successfully — drop the saved draft
      deleteFileDraft(draftKey(form.milestone_id))   // and its attached-file draft (IndexedDB)
      await loadAll()
      if (failed.length) {
        // Deliverable exists and the PM was already notified; just some bills didn't attach.
        // Leave the modal open with a clear note so the consultant can re-add them via Edit.
        setError(`Deliverable submitted, but ${failed.length} bill(s) couldn't be uploaded (${failed.join(', ')}). Open the deliverable → Edit to re-attach them.`)
      } else {
        setShowSubmit(false)
      }
    } catch (e) {
      if (e.response?.status === 413) {
        setError(`The deliverable file is larger than the ${MAX_UPLOAD_MB} MB limit. Please compress it and try again.`)
      } else {
        const detail = e.response?.data?.detail || 'Failed to submit deliverable.'
        setError(detail)
        // Over-budget rejected at the CREATE step (e.g. the incidental "Other" amount tipped
        // the milestone over its cap) → offer the same one-tap Notify-PM as the per-bill path.
        if (e.response?.status === 422 && /budget/i.test(detail)) {
          setVerifyOpen(false); setShowSubmit(false)
          setBudgetPmRequest({ milestoneId: Number(form.milestone_id), total: parseMoney(detail) })
        }
      }
    } finally {
      setSubmitting(false)
    }
  }

  // ── Review (PM / Admin) ────────────────────────────────────────────────
  const review = async (deliverableId, decision) => {
    setError(null)
    // A reject / request-revision is disruptive for the consultant — confirm first.
    if (decision === 'rejected' || decision === 'revision_requested') {
      const label = decision === 'rejected' ? 'reject this deliverable' : 'request a revision'
      if (!window.confirm(`Are you sure you want to ${label}? The consultant will be notified.`)) return
    }
    setReviewBusy(decision)
    try {
      const fd = new FormData()
      fd.append('decision', decision)
      if (reviewNotes) fd.append('notes', reviewNotes)
      if (reviewFile && decision === 'revision_requested') fd.append('file', reviewFile)
      await api.post(`/deliverables/${deliverableId}/review`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      setReviewingId(null)
      setReviewNotes('')
      setReviewFile(null)
      await loadAll()
    } catch (e) {
      setError(e.response?.data?.detail || 'Review failed.')
    } finally {
      setReviewBusy(null)
    }
  }

  // ── v3.6 per-bill review ──────────────────────────────────────────────────────────────
  // Each bill is judged on its own: approve the visa, send the inflated food claim back with a
  // reason. Selection is per bill id so "select all" can approve a whole batch at once.
  const [billSel, setBillSel] = useState([])          // selected bill ids
  const [billBusy, setBillBusy] = useState(null)      // bill id (or 'bulk') currently saving
  const toggleBill = (id) => setBillSel(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])

  const reviewBill = async (billId, decision) => {
    setError(null)
    let comment = null
    if (decision !== 'approved') {
      // The backend rejects a send-back without a reason (422); ask for it up front so the
      // reviewer never sees that error.
      comment = window.prompt(
        decision === 'rejected'
          ? 'Why is this bill rejected? (required — the consultant sees this)'
          : 'What does the consultant need to do? e.g. "More justification is needed for this expense" (required)'
      )
      if (comment === null) return                     // cancelled
      if (!comment.trim() || comment.trim().length < 3) {
        setError('A reason is required when sending a bill back.')
        return
      }
    }
    setBillBusy(billId)
    try {
      await api.put(`/deliverables/reimbursements/${billId}/review`, { decision, comment })
      setBillSel(prev => prev.filter(x => x !== billId))
      await loadAll()
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not review that bill.')
    } finally {
      setBillBusy(null)
    }
  }

  // Consultant answers a query on ONE bill: correct the amount / add justification / replace the
  // receipt. The same row is updated so the reviewer's comment stays attached to it as history.
  const [amendingBill, setAmendingBill] = useState(null)   // the bill being amended
  const [amendAmount, setAmendAmount] = useState('')       // USD — what the invoice totals use
  const [amendNote, setAmendNote] = useState('')
  const [amendFile, setAmendFile] = useState(null)
  // Same AI read + verify step as the submit flow, but scoped to THIS one bill.
  const [amendScan, setAmendScan] = useState(null)         // null | 'loading' | 'failed' | 'done'
  const [amendOrig, setAmendOrig] = useState('')           // amount as printed on the receipt
  const [amendCur, setAmendCur] = useState('')
  const [amendDate, setAmendDate] = useState('')

  const openAmend = (rf) => {
    setAmendingBill(rf)
    setAmendAmount(rf.amount != null ? String(rf.amount) : '')
    setAmendNote(rf.note || '')
    setAmendFile(null)
    setAmendScan(null)
    setAmendOrig(rf.amount_original != null ? String(rf.amount_original) : '')
    setAmendCur(rf.currency || '')
    setAmendDate(rf.expense_date || '')
  }

  // Read a replacement receipt with the same extractor used at submit time, then prefill the
  // figures for the consultant to confirm. Without this the amount stayed at the OLD value while
  // a new receipt sat underneath it — the numbers and the document silently disagreed.
  const scanAmendFile = async (f) => {
    setAmendFile(f)
    if (!f) { setAmendScan(null); return }
    setAmendScan('loading')
    try {
      const fd = new FormData()
      fd.append('file', f)
      const r = await api.post('/deliverables/parse-bill', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      const d = r.data || {}
      setAmendScan('done')
      if (d.amount != null) setAmendOrig(String(d.amount))
      if (d.currency) setAmendCur(d.currency)
      if (d.date) setAmendDate(d.date)
      if (d.amount_usd != null) setAmendAmount(String(d.amount_usd))
      else if (d.amount != null && (!d.currency || d.currency === 'USD')) setAmendAmount(String(d.amount))
      if (d.description && !amendNote.trim()) setAmendNote(d.description)
    } catch {
      setAmendScan('failed')   // manual entry still works — extraction is an assist, not a gate
    }
  }

  // Re-convert to USD whenever the consultant corrects the printed amount / currency / date.
  const reconvertAmend = async (next = {}) => {
    const orig = next.orig !== undefined ? next.orig : amendOrig
    const cur = next.cur !== undefined ? next.cur : amendCur
    const dt = next.date !== undefined ? next.date : amendDate
    const amt = parseFloat(orig)
    if (isNaN(amt) || !cur) return
    try {
      const r = await api.get('/deliverables/fx-convert', { params: { amount: amt, currency: cur, date: dt || '' } })
      if (r.data?.usd != null) setAmendAmount(String(r.data.usd))
    } catch { /* keep whatever USD is there */ }
  }
  const submitAmend = async () => {
    if (!amendingBill) return
    setError(null)
    setBillBusy(amendingBill.id)
    try {
      const fd = new FormData()
      if (amendAmount !== '') fd.append('amount', amendAmount)
      if (amendOrig !== '') fd.append('amount_original', amendOrig)
      if (amendCur) fd.append('currency', amendCur)
      if (amendDate) fd.append('expense_date', amendDate)
      if (amendNote.trim()) fd.append('note', amendNote.trim())
      if (amendFile) fd.append('file', amendFile)
      await api.put(`/deliverables/reimbursements/${amendingBill.id}/amend`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      setAmendingBill(null)
      await loadAll()
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not amend that bill.')
    } finally {
      setBillBusy(null)
    }
  }

  const bulkReviewBills = async (decision) => {
    if (!billSel.length) return
    setError(null)
    let comment = null
    if (decision !== 'approved') {
      comment = window.prompt(`Reason for sending ${billSel.length} bill(s) back (required — the consultant sees this):`)
      if (comment === null) return
      if (!comment.trim() || comment.trim().length < 3) {
        setError('A reason is required when sending bills back.')
        return
      }
    } else if (!window.confirm(`Approve ${billSel.length} selected bill(s)?`)) {
      return
    }
    setBillBusy('bulk')
    try {
      const r = await api.post('/deliverables/reimbursements/bulk-review', { bill_ids: billSel, decision, comment })
      if (r.data?.skipped?.length) {
        setError(`${r.data.skipped.length} bill(s) were skipped — already invoiced, or not yours to review.`)
      }
      setBillSel([])
      await loadAll()
    } catch (e) {
      setError(e.response?.data?.detail || 'Bulk review failed.')
    } finally {
      setBillBusy(null)
    }
  }

  // Admin or PM approves invoice after consultant confirms
  const managementApprove = async (milestoneId) => {
    setError(null)
    try {
      const r = await api.post(`/deliverables/invoices/management-approve/${milestoneId}`)
      await loadAll()
      if (r.data?.ready_for_finance) {
        alert('Both Admin and PM approved! Finance has been notified.')
      } else {
        alert(r.data?.detail || 'Your approval recorded.')
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Approval failed')
    }
  }

  // PM/Admin triggers invoice for a completed milestone
  const triggerInvoice = async (milestoneId) => {
    setError(null)
    try {
      await api.post(`/deliverables/invoices/trigger/${milestoneId}`)
      await loadAll()
      alert('Invoice triggered! Consultant has been notified to confirm.')
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to trigger invoice')
    }
  }

  // Consultant edits their own not-yet-approved deliverable (Mam's ask — FULL control before
  // review): the submit modal reopens in edit mode with title/description prefilled, the current
  // files + bills listed (removable), new bills addable with extraction preview, and Save
  // resubmits + re-notifies the PM/Admin so the review → invoice flow continues.
  const [editingDeliverable, setEditingDeliverable] = useState(null)
  const [editBills, setEditBills] = useState([])

  // Auto-save the text fields as a draft while the Submit modal is open (not in edit mode).
  // Declared here — AFTER editingDeliverable — because its dep array reads editingDeliverable,
  // and a dep array is evaluated during render (a forward-ref would be a TDZ crash → white screen).
  useEffect(() => {
    if (!showSubmit || editingDeliverable || !form.milestone_id) return
    const hasContent = form.title || form.description || form.otherAmount || form.otherNote
    if (!hasContent) return
    try {
      localStorage.setItem(draftKey(form.milestone_id), JSON.stringify({
        title: form.title, description: form.description, otherAmount: form.otherAmount, otherNote: form.otherNote,
      }))
    } catch { /* ignore quota */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSubmit, editingDeliverable, form.milestone_id, form.title, form.description, form.otherAmount, form.otherNote])
  const [editExtraFiles, setEditExtraFiles] = useState([])
  const openEdit = (d) => {
    setForm({
      milestone_id: d.milestone_id, title: d.title, description: d.description || '',
      files: [], reimb: { ...EMPTY_REIMB }, otherAmount: '', otherNote: '',
    })
    setEditBills(d.reimbursements || [])
    setEditExtraFiles(d.extra_files || [])
    setEditingDeliverable(d)
    setShowSubmit(true)
  }
  const saveEdit = async () => {
    if (!form.title.trim()) { setError('Title is required.'); return }
    // Each file uploads in its own small request (gateway caps bodies at ~50 MB) — guard
    // any single file that's too large on its own.
    const files = await shrinkAll(form.files)
    const tooBig = oversizeError(files)
    if (tooBig) { setError(tooBig); return }
    setSubmitting(true); setError(null)
    try {
      // 1. New bills — send each as its OWN small request so no single upload nears the cap.
      const hasNewBills = Object.values(form.reimb).some(files => files.length > 0)
      const hasOther = form.otherAmount !== '' && !isNaN(parseFloat(form.otherAmount))
      if ((hasNewBills || hasOther) && milestoneIsReimbursable(form.milestone_id)) {
        const bills = []
        Object.entries(form.reimb).forEach(([cat, files]) => (files || []).forEach(f => bills.push({ cat, f })))
        const failed = []
        let budgetErr = null   // set if a bill is rejected for exceeding the milestone budget
        for (let i = 0; i < bills.length; i++) {
          const { cat, f } = bills[i]
          if (oversizeError([f])) { failed.push(f.name); continue }
          const bfd = new FormData()
          bfd.append('reimbursement_files', f)
          bfd.append('reimbursement_categories', cat)
          // Consultant-verified amount/currency/date (converted at the bill's date).
          const b = effBill(f)
          if (b.amount != null && b.amount !== '') bfd.append('reimbursement_amounts', b.amount)
          if (b.currency) bfd.append('reimbursement_currencies', b.currency)
          if (b.date) bfd.append('reimbursement_dates', b.date)
          if (b.note) bfd.append('reimbursement_notes', b.note)
          if (b.vendor) bfd.append('reimbursement_vendors', b.vendor)
          // Attach the typed incidental-other amount to the first bill request.
          if (i === 0 && hasOther) {
            bfd.append('incidental_other_amount', parseFloat(form.otherAmount))
            if (form.otherNote) bfd.append('incidental_other_note', form.otherNote)
          }
          try { await api.post(`/deliverables/${editingDeliverable.id}/bills`, bfd, { headers: { 'Content-Type': 'multipart/form-data' } }) }
          catch (be) {
            // 422 = milestone budget would be exceeded — surface it clearly, not as an upload error.
            if (be.response?.status === 422) { budgetErr = be.response.data?.detail || 'This milestone budget would be exceeded.'; break }
            failed.push(f.name)
          }
        }
        // No bill files but a typed incidental-other amount → send it in its own tiny request.
        if (!budgetErr && !bills.length && hasOther) {
          const bfd = new FormData()
          bfd.append('incidental_other_amount', parseFloat(form.otherAmount))
          if (form.otherNote) bfd.append('incidental_other_note', form.otherNote)
          try { await api.post(`/deliverables/${editingDeliverable.id}/bills`, bfd, { headers: { 'Content-Type': 'multipart/form-data' } }) }
          catch (be) { if (be.response?.status === 422) budgetErr = be.response.data?.detail || 'This milestone budget would be exceeded.'; else throw be }
        }
        if (budgetErr) {
          // The backend already removed the over-budget bills — just surface the message and
          // offer the one-tap "Notify my PM". Keep the deliverable as-is.
          setVerifyOpen(false); setShowSubmit(false); setEditingDeliverable(null)
          await loadAll()
          setError(budgetErr)
          setBudgetPmRequest({ milestoneId: Number(form.milestone_id), total: parseMoney(budgetErr) })
          setSubmitting(false)
          return
        }
        if (failed.length) {
          setError(`${failed.length} bill(s) couldn't be uploaded (${failed.join(', ')}). Try again or compress them.`)
          setSubmitting(false)
          return
        }
      }
      // 2. Replacement main file, if one was attached.
      if (files[0]) {
        const fd2 = new FormData()
        fd2.append('file', files[0])
        await api.post(`/deliverables/${editingDeliverable.id}/replace-file`, fd2, { headers: { 'Content-Type': 'multipart/form-data' } })
      }
      // 3. Save texts + resubmit → PM/Admin re-notified (bell + email), flow continues.
      await api.put(`/deliverables/${editingDeliverable.id}`, {
        title: form.title.trim(), description: form.description, resubmit: true,
      })
      setShowSubmit(false); setEditingDeliverable(null)
      await loadAll()
      alert('Deliverable updated and resubmitted — the PM and Admin have been notified to review it.')
    } catch (e) {
      if (e.response?.status === 413) {
        setError(`One of the files is larger than the ${MAX_UPLOAD_MB} MB limit. Please remove or compress it and try again.`)
      } else {
        const detail = e.response?.data?.detail || 'Failed to save the changes.'
        setError(detail)
        if (e.response?.status === 422 && /budget/i.test(detail)) {
          setVerifyOpen(false); setShowSubmit(false); setEditingDeliverable(null)
          setBudgetPmRequest({ milestoneId: Number(form.milestone_id), total: parseMoney(detail) })
        }
      }
    } finally {
      setSubmitting(false)
    }
  }
  const withdrawDeliverable = async () => {
    if (!window.confirm('Delete this deliverable entirely? Its attached bills are removed too. This cannot be undone.')) return
    try {
      await api.delete(`/deliverables/${editingDeliverable.id}`)
      setShowSubmit(false); setEditingDeliverable(null)
      await loadAll()
    } catch (e) { setError(e.response?.data?.detail || 'Could not delete the deliverable.') }
  }
  const deleteBill = async (rfId) => {
    if (!window.confirm('Remove this reimbursement bill? Its amount will no longer count toward your invoice.')) return
    try {
      await api.delete(`/deliverables/reimbursements/${rfId}`)
      setEditBills(prev => prev.filter(b => b.id !== rfId))
      await loadAll()
    } catch (e) { setError(e.response?.data?.detail || 'Could not remove the bill.') }
  }
  const removeExtraFile = async (fileId) => {
    if (!window.confirm('Remove this additional file from the deliverable?')) return
    try {
      await api.delete(`/deliverables/files/${fileId}`)
      setEditExtraFiles(prev => prev.filter(x => x.id !== fileId))
      await loadAll()
    } catch (e) { setError(e.response?.data?.detail || 'Could not remove the file.') }
  }

  // Batch: tick several submitted deliverables and approve them all + generate the invoice for
  // each milestone that becomes fully approved, in one action.
  const toggleBatch = (deliverableId) =>
    setBatchIds(prev => prev.includes(deliverableId) ? prev.filter(x => x !== deliverableId) : [...prev, deliverableId])
  const batchApprove = async () => {
    if (batchIds.length === 0) return
    if (!window.confirm(`Approve ${batchIds.length} selected deliverable(s) and generate the invoice for each completed milestone?`)) return
    setBatching(true); setError(null)
    try {
      const r = await api.post('/deliverables/batch-approve', { deliverable_ids: batchIds, generate_invoice: true })
      setBatchIds([])
      await loadAll()
      alert(r.data?.detail || 'Batch approval complete.')
    } catch (e) {
      setError(e.response?.data?.detail || 'Batch approval failed.')
    } finally {
      setBatching(false)
    }
  }

  // Opens the Confirm Invoice modal: fetches banking + (if reimbursable) the auto-computed
  // claim total from attached bills, so the consultant reviews everything in one place before
  // typing their name / signing, instead of the old window.confirm/window.prompt chain.
  const confirmInvoice = async (milestoneId) => {
    setError(null)
    let banking = null
    try {
      const b = await api.get('/users/me/banking')
      if (b.data?.has_banking) banking = b.data
    } catch { /* fall through — backend 428 gate still protects payment */ }

    const budgetType = project?.budget_type || 'Fixed'
    const reimbursable = milestoneIsReimbursable(milestoneId)
    let reimbTotal = 0
    let reimbCount = 0
    let reimbOriginal = 0
    if (reimbursable) {
      try {
        const inv = await api.get(`/deliverables/invoices/${milestoneId}/detail`)
        reimbTotal = inv.data?.reimbursement_total_usd || 0
        reimbCount = inv.data?.reimbursement_count || 0
        reimbOriginal = inv.data?.reimbursement_total_original || 0
      } catch { /* fall through to manual entry in the modal */ }
    }

    // Pre-fill the claim amount from the USD total; if USD couldn't be auto-read but bills exist,
    // fall back to the original-currency total so the consultant isn't left with a blank field.
    const prefill = reimbTotal > 0 ? reimbTotal : (reimbCount > 0 && reimbOriginal > 0 ? reimbOriginal : 0)
    setConfirmClaimAmount(prefill > 0 ? String(prefill) : '')
    setConfirmSigName('')
    setConfirmSigData(null)
    setConfirmInvoiceModal({ milestoneId, budgetType, reimbursable, reimbTotal, reimbCount, banking })
  }

  const submitInvoiceConfirmation = async () => {
    if (!confirmInvoiceModal) return
    const { milestoneId, reimbursable, reimbTotal } = confirmInvoiceModal
    if (!confirmSigName.trim()) { setError('Please type your full name to sign.'); return }

    const body = { signature_name: confirmSigName.trim(), signature_data: confirmSigData }
    if (reimbursable && reimbTotal <= 0) {
      const amount = parseFloat(confirmClaimAmount)
      if (isNaN(amount) || amount < 0) { setError('Please enter a valid claimed amount.'); return }
      body.claimed_amount = amount
    }

    setConfirmSubmitting(true)
    setError(null)
    try {
      await api.post(`/deliverables/invoices/confirm/${milestoneId}`, body)
      setConfirmInvoiceModal(null)
      await loadAll()
      alert('Invoice confirmed and signed! Admin, PM and Finance have been notified.')
    } catch (e) {
      if (e.response?.status === 428) {
        setConfirmInvoiceModal(null)
        alert(e.response?.data?.detail || 'Please add your bank details before approving this invoice.')
        // Return straight back to this milestone's confirm dialog after banking is saved.
        navigate(`/profile-settings?edit=banking&return=${encodeURIComponent(`/projects/${project?.id}?tab=deliverables&confirmInvoice=${milestoneId}`)}`)
        return
      }
      setError(e.response?.data?.detail || 'Failed to confirm invoice')
    } finally {
      setConfirmSubmitting(false)
    }
  }

  // Attach one or more files to a milestone straight from its card (no need to open the
  // chat). Each file is posted to the milestone thread, so PM/Consultant/Admin can View/Download.
  const [attachingMs, setAttachingMs] = useState(null)
  const attachToMilestone = async (milestoneId, fileList) => {
    const files = Array.from(fileList || [])
    if (!files.length) return
    setAttachingMs(milestoneId)
    setError(null)
    try {
      for (const f of files) {
        const fd = new FormData()
        fd.append('content', `📎 ${f.name}`)
        fd.append('file', f)
        await api.post(`/chat/milestone/${milestoneId}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      }
      await loadAll()
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not attach file(s).')
    } finally {
      setAttachingMs(null)
    }
  }

  // View/Download a reimbursement bill (keeps original name)
  const openReimbursement = async (rfId, mode = 'attachment', origName = null) => {
    try {
      const r = await api.get(`/deliverables/reimbursements/${rfId}/download`, {
        params: { disposition: mode }, responseType: 'blob',
      })
      const url = URL.createObjectURL(r.data)
      if (mode === 'inline') {
        window.open(url, '_blank')
      } else {
        const a = document.createElement('a')
        a.href = url; a.download = origName || `bill_${rfId}`
        document.body.appendChild(a); a.click(); document.body.removeChild(a)
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000)
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not open bill.')
    }
  }

  // View/download an extra deliverable content file (multi-file submissions).
  const openExtraFile = async (fileId, mode = 'attachment', origName = null) => {
    try {
      const r = await api.get(`/deliverables/files/${fileId}/download`, {
        params: { disposition: mode }, responseType: 'blob',
      })
      if (r.headers['content-type']?.includes('application/json')) {
        const json = JSON.parse(await r.data.text())
        if (json?.url) { window.open(json.url, '_blank'); return }
      }
      const url = URL.createObjectURL(r.data)
      if (mode === 'inline') { window.open(url, '_blank') }
      else {
        const a = document.createElement('a')
        a.href = url; a.download = origName || `file_${fileId}`
        document.body.appendChild(a); a.click(); document.body.removeChild(a)
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000)
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not open file.')
    }
  }

  const downloadBriefFile = async (milestoneId) => {
    try {
      const resp = await api.get(`/projects/milestones/${milestoneId}/brief/download`, { responseType: 'blob' })
      const url = URL.createObjectURL(resp.data)
      window.open(url, '_blank')
      setTimeout(() => URL.revokeObjectURL(url), 60_000)
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not download brief.')
    }
  }

  // PM's revision-notes file (S-revision-upload) — download for consultant/PM/Admin.
  const downloadRevisionBrief = async (deliverableId, filename) => {
    try {
      const r = await api.get(`/deliverables/${deliverableId}/revision-brief/download`, { responseType: 'blob' })
      if (r.headers['content-type']?.includes('application/json')) {
        const json = JSON.parse(await r.data.text())
        if (json?.url) { window.open(json.url, '_blank'); return }
      }
      const url = URL.createObjectURL(r.data)
      const a = document.createElement('a')
      a.href = url; a.download = filename || `revision-notes`
      a.click()
      setTimeout(() => URL.revokeObjectURL(url), 60_000)
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not download revision notes.')
    }
  }

  // mode: 'inline' = View in a new tab, 'attachment' = Download. Either way the file
  // keeps its ORIGINAL uploaded name (passed in as origName for local-file downloads).
  const openFile = async (deliverableId, mode = 'attachment', origName = null) => {
    try {
      const r = await api.get(`/deliverables/${deliverableId}/download`, {
        params: { disposition: mode },
        responseType: 'blob',
      })
      // S3-backed files come back as JSON with a presigned URL (already carries the
      // right name + inline/attachment behaviour) — just open it.
      if (r.headers['content-type']?.includes('application/json')) {
        const json = JSON.parse(await r.data.text())
        if (json?.url) { window.open(json.url, '_blank'); return }
      }
      // Local file blob.
      const url = URL.createObjectURL(r.data)
      if (mode === 'inline') {
        window.open(url, '_blank')   // browser views pdf/img/video; office files download
      } else {
        const a = document.createElement('a')
        a.href = url
        a.download = origName || `deliverable_${deliverableId}`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000)
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not open file.')
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
      </div>
    )
  }

  // Compute dot color + label for a milestone card — role-aware, state-driven
  const getMilestoneDot = (ms, items) => {
    const hasSubmitted = items.some(d => ['Submitted', 'Revision Requested'].includes(d.status))
    const allApproved = items.length > 0 && items.every(d => d.status === 'Approved')
    const hasRevision = items.some(d => d.status === 'Revision Requested')
    const paid = ms.status === 'Paid'

    if (paid) return { color: 'bg-green-500', label: '✓ Paid', pulse: false }

    if (isConsultant) {
      if (hasRevision)
        return { color: 'bg-red-500', label: '⚠ Revision needed — resubmit', pulse: true }
      if (ms.invoice_triggered && !ms.consultant_invoice_confirmed)
        return { color: 'bg-amber-400', label: '👆 Confirm invoice required', pulse: true }
      if (ms.consultant_invoice_confirmed && !(ms.admin_invoice_approved && ms.pm_invoice_approved))
        return { color: 'bg-blue-400', label: '⏳ Awaiting management approval', pulse: false }
      if (ms.admin_invoice_approved && ms.pm_invoice_approved)
        return { color: 'bg-green-400', label: '✓ Approved — Finance paying', pulse: false }
      return null
    }

    if (isPMOrAdmin) {
      if (hasSubmitted)
        return { color: 'bg-amber-400', label: '👆 Review deliverable', pulse: true }
      // Ready to invoice: at least one approved AND all have been reviewed (no more "Submitted" pending)
      const hasAnyApproved = items.some(d => d.status === 'Approved')
      const allReviewed = items.every(d => d.status !== 'Submitted')
      if (hasAnyApproved && allReviewed && !ms.invoice_triggered)
        return { color: 'bg-blue-400', label: '👆 Generate invoice', pulse: true }
      if (ms.consultant_invoice_confirmed) {
        const myApproved = user?.role === 'Admin' ? ms.admin_invoice_approved : ms.pm_invoice_approved
        if (!myApproved)
          return { color: 'bg-amber-400', label: '👆 Approve invoice', pulse: true }
      }
      if (ms.admin_invoice_approved && ms.pm_invoice_approved)
        return { color: 'bg-green-500', label: '✓ Finance can pay', pulse: false }
      return null
    }

    return null
  }

  // Group by milestone — merge brief info from deliverable response into milestone object
  const byMilestone = {}
  for (const m of milestones) byMilestone[m.id] = { milestone: m, items: [] }
  for (const d of deliverables) {
    if (byMilestone[d.milestone_id]) {
      // Enrich milestone with brief info if backend returned it in deliverable data
      if (d.milestone_has_brief !== undefined) byMilestone[d.milestone_id].milestone.has_brief = d.milestone_has_brief
      if (d.milestone_brief_instructions) byMilestone[d.milestone_id].milestone.brief_instructions = d.milestone_brief_instructions
      if (d.milestone_payment) byMilestone[d.milestone_id].milestone.payment_amount = d.milestone_payment
      if (d.milestone_invoice_triggered !== undefined) byMilestone[d.milestone_id].milestone.invoice_triggered = d.milestone_invoice_triggered
      if (d.milestone_consultant_confirmed !== undefined) byMilestone[d.milestone_id].milestone.consultant_invoice_confirmed = d.milestone_consultant_confirmed
      if (d.milestone_admin_approved !== undefined) byMilestone[d.milestone_id].milestone.admin_invoice_approved = d.milestone_admin_approved
      if (d.milestone_pm_approved !== undefined) byMilestone[d.milestone_id].milestone.pm_invoice_approved = d.milestone_pm_approved
      if (d.consultant_name) byMilestone[d.milestone_id].milestone.consultant_name = d.consultant_name
      byMilestone[d.milestone_id].items.push(d)
    } else {
      byMilestone[d.milestone_id] = {
        milestone: {
          id: d.milestone_id,
          title: d.milestone_title || `Milestone #${d.milestone_id}`,
          payment_amount: d.milestone_payment,
          has_brief: d.milestone_has_brief,
          brief_instructions: d.milestone_brief_instructions,
        },
        items: [d]
      }
    }
  }

  // On a multi-consultant project the PM/Admin sees several milestone cards owned by
  // different consultants — label each card with its owner so it's clear whose work it is.
  // (Only shown to PM/Admin, and only when >1 distinct consultant actually owns milestones.)
  const distinctOwnerCount = new Set(
    Object.values(byMilestone).map(({ milestone }) => milestone.consultant_name).filter(Boolean)
  ).size
  const showConsultantLabels = isPMOrAdmin && distinctOwnerCount > 1

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{tDel('project_deliverables')}</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            {isConsultant
              ? 'Submit your work milestone-by-milestone. PM will review each.'
              : 'Review what the consultant has submitted. Approve / request revision / reject.'}
          </p>
        </div>
        {isMyProject && milestones.length > 0 && !isArchived && (
          hasActiveContract === false ? (
            // Contract not yet Active — consultant must wait
            <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              🔒 Deliverables locked — contract must be signed &amp; active first
            </div>
          ) : (
            <button
              onClick={openSubmit}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus className="w-4 h-4" /> Submit Deliverables &amp; Bills (Reimbursements)
            </button>
          )
        )}
        {/* For a consultant who is stuck. Same modal and same flow as theirs — only the
            attribution differs, and the banner inside says whose work it is. */}
        {submittableForOthers && (
          <button
            onClick={openSubmit}
            title="Upload and submit on behalf of the consultant who owns the milestone"
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium border border-blue-200 text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100"
          >
            <Plus className="w-4 h-4" /> Submit for the consultant
          </button>
        )}
      </div>

      {/* Batch approve bar — appears once the PM/Admin ticks one or more submitted
          deliverables (checkbox on each Submitted card below). One click approves all
          selected and generates the invoice for each milestone that becomes fully approved. */}
      {isPMOrAdmin && !isArchived && batchIds.length > 0 && (
        <div className="flex items-center justify-between gap-3 bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-2.5">
          <span className="text-sm text-emerald-800 font-medium">{batchIds.length} deliverable(s) selected</span>
          <div className="flex items-center gap-2">
            <button onClick={() => setBatchIds([])} className="px-3 py-1.5 text-xs text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">{tDel('clear')}</button>
            <button
              onClick={batchApprove}
              disabled={batching}
              className="px-4 py-1.5 text-sm font-semibold bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
            >
              {batching ? 'Approving…' : '✓ Approve selected & generate invoice(s)'}
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-2.5 rounded-lg flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <p>{error}</p>
            {budgetPmRequest && (
              <button
                onClick={notifyPmBudget}
                disabled={notifyingPm}
                className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50"
              >
                {notifyingPm ? 'Notifying…' : '📨 Notify my PM (chat + email)'}
              </button>
            )}
          </div>
          <button onClick={() => { setError(null); setBudgetPmRequest(null) }} className="text-red-700 hover:text-red-900 shrink-0"><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* Milestone board — every milestone's current phase at a glance (scope × status) */}
      {milestones.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">{tDel('milestone_board')}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
            {milestones.map(m => {
              const scope = m.scope_status || 'Unassigned'
              const scopeColor = scope === 'Approved' ? 'bg-green-50 text-green-700 border-green-200'
                : scope === 'Accepted' ? 'bg-blue-50 text-blue-700 border-blue-200'
                : scope === 'Offered' ? 'bg-amber-50 text-amber-700 border-amber-200'
                : 'bg-gray-50 text-gray-500 border-gray-200'
              const st = m.status || 'Pending'
              const stColor = st === 'Paid' ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                : st === 'Approved' ? 'bg-green-50 text-green-700 border-green-200'
                : 'bg-gray-50 text-gray-500 border-gray-200'
              return (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => document.getElementById(`ms-${m.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
                  className="text-left p-3 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50/40 transition"
                >
                  <p className="text-sm font-medium text-gray-800 truncate">{m.title}</p>
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    <span className={`text-[10px] font-medium border rounded-full px-2 py-0.5 ${scopeColor}`}>{scope === 'Unassigned' ? 'Not Assigned' : scope}</span>
                    <span className={`text-[10px] font-medium border rounded-full px-2 py-0.5 ${stColor}`}>{st}</span>
                    {m.payment_mode === 'Actual' && <span className="text-[10px] font-medium border border-amber-200 bg-amber-50 text-amber-700 rounded-full px-2 py-0.5">{tDel('actuals')}</span>}
                    {(m.invoice_round || 1) > 1 && <span className="text-[10px] font-medium border border-indigo-200 bg-indigo-50 text-indigo-700 rounded-full px-2 py-0.5">Round {m.invoice_round}</span>}
                  </div>
                </button>
              )
            })}
          </div>
        </div>
      )}

      {/* Empty state — no milestones */}
      {milestones.length === 0 && (
        <div className="text-center py-10 bg-gray-50 rounded-lg border border-dashed border-gray-300">
          <p className="text-sm text-gray-500">This project has no milestones yet — PM/Admin needs to add them first.</p>
        </div>
      )}

      {/* Empty state — milestones exist but no deliverables */}
      {milestones.length > 0 && deliverables.length === 0 && (
        <div className="text-center py-10 bg-gray-50 rounded-lg border border-dashed border-gray-300">
          <FileText className="w-10 h-10 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500">
            {isMyProject ? 'You haven\'t submitted any deliverables yet. Click "Submit Deliverables & Bills" above to start.' : 'Consultant hasn\'t submitted any deliverables yet.'}
          </p>
        </div>
      )}

      {/* Deliverables grouped by milestone */}
      {Object.values(byMilestone).map(({ milestone, items }) => {
        const dot = getMilestoneDot(milestone, items)
        return (
        <div key={milestone.id} id={`ms-${milestone.id}`} className={`bg-white border rounded-xl overflow-hidden transition-all ${String(milestone.id) === highlighted ? 'ring-2 ring-blue-400 border-blue-300' : (dot?.pulse ? 'border-amber-300 shadow-sm' : 'border-gray-200')}`}>
          <div className={`px-4 py-3 border-b ${dot ? 'bg-amber-50/40' : 'bg-gray-50'} border-gray-200`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  {/* Milestone action dot */}
                  {dot && (
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold text-white ${dot.color} ${dot.pulse ? 'animate-pulse' : ''} shrink-0`}
                      title={dot.label}
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-white/70" />
                      {dot.label}
                    </span>
                  )}
                </div>
                {showConsultantLabels && milestone.consultant_name && (
                  <div className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full bg-indigo-50 border border-indigo-200 text-[11px] font-medium text-indigo-700">
                    👤 {milestone.consultant_name}
                  </div>
                )}
                <h4 className="text-sm font-semibold text-gray-800 mt-1">{milestone.title}</h4>
                {milestone.payment_amount > 0 && (
                  <p className="text-[11px] text-gray-500 mt-0.5">Payment on completion: ${usd2(milestone.payment_amount)}</p>
                )}
                {milestone.brief_instructions && (
                  <p className="text-xs text-blue-800 bg-blue-50 border border-blue-100 rounded px-2 py-1 mt-1.5">
                    📋 <span className="font-medium">{tDel('pm_instructions')}</span> {milestone.brief_instructions}
                  </p>
                )}
              </div>
              <div className="flex flex-col items-end gap-1 shrink-0">
                <span className="text-xs text-gray-500">{items.length} deliverable{items.length !== 1 ? 's' : ''}</span>
                {milestone.has_brief && (
                  <button
                    onClick={() => downloadBriefFile(milestone.id)}
                    className="text-xs px-2 py-0.5 bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    📎 Download Brief
                  </button>
                )}
                {/* Attach files to this milestone's thread — header action, aligned with Download Brief */}
                {(isPMOrAdmin || isMyProject) && !isArchived && (
                  attachingMs === milestone.id ? (
                    <span className="text-xs text-gray-400 flex items-center gap-1 px-2 py-0.5"><Loader2 className="w-3 h-3 animate-spin" /> Uploading…</span>
                  ) : (
                    <label
                      className="text-xs px-2 py-0.5 border border-blue-200 bg-blue-50 text-blue-700 rounded cursor-pointer hover:bg-blue-100 inline-flex items-center gap-1"
                      title={tDel('t_shared_in_thread')}
                    >
                      <Upload className="w-3 h-3" /> Attach file(s)
                      <input type="file" multiple className="hidden"
                        onChange={(e) => { attachToMilestone(milestone.id, e.target.files); e.target.value = '' }} />
                    </label>
                  )
                )}
              </div>
            </div>
          </div>
          {/* v3.1 progress billing: a Paid reimbursable milestone can be billed again in a
              fresh round. Tell the consultant they can keep submitting (within the budget). */}
          {isConsultant && isMyProject && !isArchived && milestone.status === 'Paid' && milestoneIsReimbursable(milestone.id) && (
            <div className="px-4 py-2.5 bg-emerald-50 border-t border-emerald-100 text-xs text-emerald-800">
              ✓ Round {milestone.invoice_round || 1} paid. You can submit more deliverables &amp; bills to start the next billing round on this milestone (within its total budget).
            </div>
          )}
          {/* ── Invoice action bar ── */}
          {items.length > 0 && !isArchived && (() => {
            const hasAnyApproved = items.some(d => d.status === 'Approved')
            // All reviewed = no deliverable is still waiting for PM review (Submitted state)
            const allReviewed = items.every(d => d.status !== 'Submitted')
            // "Ready to invoice" = at least one approved AND PM has reviewed all (no pending ones)
            const readyToInvoice = hasAnyApproved && allReviewed
            const ms = milestone

            return (
              <div className="px-4 py-2.5 bg-gray-50 border-t border-gray-100 flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 text-xs">
                  <span className={`w-2 h-2 rounded-full ${readyToInvoice ? 'bg-green-500' : 'bg-amber-400'}`} />
                  <span className="text-gray-600">
                    {readyToInvoice
                      ? `${items.filter(d => d.status === 'Approved').length} approved — ready to invoice`
                      : 'Deliverables pending review'}
                  </span>
                  {ms.invoice_triggered && (
                    <span className="text-blue-700 font-medium">· Invoice triggered</span>
                  )}
                  {ms.consultant_invoice_confirmed && (
                    <span className="text-green-700 font-medium">· Consultant confirmed ✓</span>
                  )}
                </div>

                {/* NEW FLOW (Mam): the CONSULTANT generates + submits their own invoice once
                    their deliverable is approved. "Generate & Submit" raises AND confirms it in
                    one step (the confirm dialog collects the signature / claimed amount), then it
                    goes to PM + Admin. */}
                {ownsMilestone(ms) && readyToInvoice && !ms.invoice_triggered && !ms.consultant_invoice_confirmed && (
                  <button
                    onClick={() => confirmInvoice(ms.id)}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    📄 Generate &amp; Submit Invoice
                  </button>
                )}

                {/* PM / Admin: Generate Invoice is now a FALLBACK (e.g. consultant unresponsive).
                    Normally the consultant does this themselves. */}
                {isPMOrAdmin && readyToInvoice && !ms.invoice_triggered && (
                  <button
                    onClick={() => triggerInvoice(ms.id)}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-white text-blue-700 border border-blue-200 rounded-lg hover:bg-blue-50"
                    title="Normally the consultant generates their own invoice — use this only if you need to raise it on their behalf"
                  >
                    📄 Generate Invoice (on consultant's behalf)
                  </button>
                )}
                {/* PM/Admin waiting for consultant after a fallback trigger */}
                {isPMOrAdmin && ms.invoice_triggered && !ms.consultant_invoice_confirmed && (
                  <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1 rounded">
                    ⏳ Awaiting consultant confirmation
                  </span>
                )}

                {/* Consultant — confirm an invoice that a PM/Admin already generated (fallback path). */}
                {isConsultant && ms.invoice_triggered && !ms.consultant_invoice_confirmed && (
                  <button
                    onClick={() => confirmInvoice(ms.id)}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    {milestoneIsReimbursable(ms.id)
                      ? '✓ Confirm Invoice'
                      : `✓ Confirm Invoice ($${usd2(ms.payment_amount)})`}
                  </button>
                )}
                {isConsultant && ms.consultant_invoice_confirmed && !ms.admin_invoice_approved && (
                  <span className="text-xs text-blue-700 font-medium">✅ You confirmed — awaiting Admin + PM approval</span>
                )}
                {isConsultant && ms.admin_invoice_approved && ms.pm_invoice_approved && (
                  <span className="text-xs text-green-700 font-medium">✅ Management approved — Finance processing payment</span>
                )}

                {/* STEP 4: Admin + PM both approve after consultant confirms */}
                {isPMOrAdmin && ms.consultant_invoice_confirmed && !(ms.admin_invoice_approved && ms.pm_invoice_approved) && (() => {
                  // Which slot THIS user fills. Stated explicitly rather than inferred by
                  // negation: an approver fills the ADMIN slot, a manager fills the PM slot, and
                  // the two grants are mutually exclusive server-side so they can't both apply.
                  const fillsAdminSlot = isApprover || (!_managesThisProject)
                  const mySlotDone = fillsAdminSlot ? ms.admin_invoice_approved : ms.pm_invoice_approved
                  const mySlotLabel = fillsAdminSlot ? 'Admin' : 'PM'
                  const otherLabel = fillsAdminSlot ? 'PM' : 'Admin'
                  const otherSlotDone = fillsAdminSlot ? ms.pm_invoice_approved : ms.admin_invoice_approved
                  return (
                    <div className="flex items-center gap-2 flex-wrap">
                      {mySlotDone ? (
                        // Names the SLOT, not the person: nothing records who filled it, so the old
                        // "You approved" wording credited the current viewer for a colleague's
                        // approval — which is exactly how the stuck sub-project invoice looked
                        // already-approved to someone who had never clicked it.
                        <span className="text-xs text-green-700 bg-green-50 border border-green-200 px-2 py-1 rounded">
                          ✅ {mySlotLabel} approval done{otherSlotDone ? '' : ` — waiting for ${otherLabel} approval`}
                        </span>
                      ) : (
                        <button
                          onClick={() => managementApprove(ms.id)}
                          className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-indigo-700 text-white rounded-lg hover:bg-indigo-800"
                        >
                          ✓ Approve Invoice → Release to Finance
                        </button>
                      )}
                    </div>
                  )
                })()}

                {/* STEP 5: Both approved → Finance will see it */}
                {isPMOrAdmin && ms.admin_invoice_approved && ms.pm_invoice_approved && (
                  <span className="text-xs text-green-700 bg-green-50 border border-green-200 px-2 py-1 rounded">
                    ✅ Both approved — Finance can now pay
                  </span>
                )}

                {/* View Invoice — always visible once triggered */}
                {ms.invoice_triggered && (
                  <button
                    onClick={() => setViewInvoiceMilestoneId(ms.id)}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
                  >
                    🧾 View Invoice
                  </button>
                )}
                {isConsultant && ms.consultant_invoice_confirmed && (
                  <span className="text-xs text-green-700 font-medium">✅ You confirmed this invoice</span>
                )}
              </div>
            )
          })()}

          {items.length === 0 ? (
            <div className="px-4 py-3 text-xs text-gray-400 italic">{tDel('none_submitted')}</div>
          ) : (
            <div className="divide-y divide-gray-100">
              {items.map(d => (
                <div key={d.id} id={`dlv-${d.id}`} className={`p-4 ${String(d.id) === highlighted ? 'ring-2 ring-blue-400 rounded-lg' : ''}`}>
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        {isPMOrAdmin && !isArchived && !d.archived_at && ['Submitted', 'Revision Requested'].includes(d.status) && (
                          <input
                            type="checkbox"
                            checked={batchIds.includes(d.id)}
                            onChange={() => toggleBatch(d.id)}
                            title={tDel('t_batch_approve')}
                            className="w-4 h-4 shrink-0 accent-emerald-600 cursor-pointer"
                          />
                        )}
                        <h5 className="font-medium text-gray-900 truncate">{d.title}</h5>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${STATUS_STYLES[d.status] || 'bg-gray-100 text-gray-600'}`}>
                          {d.status}
                        </span>
                        {d.version > 1 && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">v{d.version}</span>
                        )}
                        {d.archived_at && (
                          <span className="text-[10px] font-medium border border-indigo-200 bg-indigo-50 text-indigo-700 rounded-full px-2 py-0.5">
                            🔒 Archived {formatDate(d.archived_at)}
                          </span>
                        )}
                        {d.purged_at && (
                          <span className="text-[10px] font-medium border border-gray-300 bg-gray-100 text-gray-500 rounded-full px-2 py-0.5">{tDel('purged')}</span>
                        )}
                      </div>
                      {d.original_filename && (
                        <p className="text-xs text-gray-600 mt-1 flex items-center gap-1 truncate">
                          <FileText className="w-3 h-3 shrink-0" /> {d.original_filename}
                        </p>
                      )}
                      {d.description && (
                        <p className="text-xs text-gray-500 mt-1 line-clamp-2">{d.description}</p>
                      )}
                      <p className="text-xs text-gray-500 mt-1">
                        Submitted {formatDate(d.submission_date)}
                        {d.reviewed_at && ` · Reviewed ${formatDate(d.reviewed_at)}`}
                      </p>
                      {d.review_notes && (
                        <div className="mt-2 text-xs bg-amber-50 border border-amber-200 rounded p-2 text-amber-900">
                          <span className="font-semibold">{tDel('review_notes')}</span> {d.review_notes}
                        </div>
                      )}
                      {d.revision_brief_filename && (
                        <button
                          onClick={() => downloadRevisionBrief(d.id, d.revision_brief_filename)}
                          className="mt-2 inline-flex items-center gap-1 text-xs text-blue-700 border border-blue-200 rounded px-2 py-1 hover:bg-blue-50"
                        >
                          <Download className="w-3 h-3" /> Download PM's revision notes
                        </button>
                      )}
                      {d.extra_files?.length > 0 && (
                        <div className="mt-2 text-xs bg-blue-50 border border-blue-200 rounded p-2">
                          <p className="font-semibold text-blue-800 mb-1">Additional files ({d.extra_files.length})</p>
                          <div className="space-y-1">
                            {d.extra_files.map(xf => (
                              <div key={xf.id} className="flex items-center justify-between gap-2">
                                <span className="truncate text-blue-900 flex items-center gap-1">
                                  <FileText className="w-3 h-3 shrink-0" /> {xf.original_filename}
                                </span>
                                <span className="flex items-center gap-2 shrink-0">
                                  <button onClick={() => setViewer({ url: `/deliverables/files/${xf.id}/download`, filename: xf.original_filename, download: () => openExtraFile(xf.id, 'attachment', xf.original_filename) })} className="text-blue-600 hover:text-blue-800 flex items-center gap-0.5"><Eye className="w-3 h-3" /> View</button>
                                  {canDownload && <button onClick={() => openExtraFile(xf.id, 'attachment', xf.original_filename)} className="text-gray-600 hover:text-gray-800 flex items-center gap-0.5"><Download className="w-3 h-3" /></button>}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {d.reimbursements?.length > 0 && (() => {
                        // v3.6: each bill carries its own review state, so a reviewer can approve
                        // the visa and send an inflated food claim back. Reviewable while the
                        // invoice for this round hasn't been confirmed yet.
                        const canReviewBills = isPMOrAdmin && !isArchived && !d.archived_at
                          && !milestone.consultant_invoice_confirmed
                        const pending = d.reimbursements.filter(b => (b.review_status || 'Pending') === 'Pending')
                        const selHere = d.reimbursements.filter(b => billSel.includes(b.id))
                        const billTone = (s) => s === 'Approved'
                          ? 'bg-green-100 text-green-800 border-green-200'
                          : s === 'Revision Requested'
                            ? 'bg-amber-100 text-amber-800 border-amber-200'
                            : s === 'Rejected'
                              ? 'bg-red-100 text-red-800 border-red-200'
                              : 'bg-gray-100 text-gray-600 border-gray-200'
                        // "Pending" on its own told the consultant nothing — pending on whom, for what?
                        // Spell out who is being waited on, and what the consultant must do.
                        const billLabel = (s) => s === 'Revision Requested'
                          ? 'Amendment needed'
                          : s === 'Pending' ? 'Pending approval from PM' : s
                        return (
                        <div className="mt-2 text-xs bg-emerald-50 border border-emerald-200 rounded p-2">
                          <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
                            <p className="font-semibold text-emerald-800">
                              Reimbursement bills ({d.reimbursements.length})
                              {pending.length > 0 && canReviewBills && (
                                <span className="ml-2 font-normal text-amber-700">· {pending.length} awaiting your decision</span>
                              )}
                            </p>
                            {canReviewBills && pending.length > 1 && (
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => setBillSel(prev => {
                                    const ids = pending.map(b => b.id)
                                    const allOn = ids.every(i => prev.includes(i))
                                    return allOn ? prev.filter(i => !ids.includes(i)) : [...new Set([...prev, ...ids])]
                                  })}
                                  className="text-emerald-800 underline hover:no-underline"
                                >
                                  {pending.every(b => billSel.includes(b.id)) ? 'Clear selection' : 'Select all pending'}
                                </button>
                                {selHere.length > 0 && (
                                  <>
                                    <button
                                      disabled={billBusy === 'bulk'}
                                      onClick={() => bulkReviewBills('approved')}
                                      className="px-2 py-0.5 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                                    >
                                      {billBusy === 'bulk' ? 'Saving…' : `✓ Approve ${selHere.length}`}
                                    </button>
                                    <button
                                      disabled={billBusy === 'bulk'}
                                      onClick={() => bulkReviewBills('revision_requested')}
                                      className="px-2 py-0.5 rounded bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50"
                                    >
                                      Send back {selHere.length}
                                    </button>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="space-y-1">
                            {d.reimbursements.map(rf => {
                              const st = rf.review_status || 'Pending'
                              return (
                              <div key={rf.id} className="border-b border-emerald-100 last:border-0 pb-1 last:pb-0">
                                <div className="flex items-center justify-between gap-2">
                                  <span className="truncate text-emerald-900 flex items-center gap-1">
                                    {canReviewBills && st === 'Pending' && (
                                      <input
                                        type="checkbox"
                                        checked={billSel.includes(rf.id)}
                                        onChange={() => toggleBill(rf.id)}
                                        className="w-3 h-3 shrink-0 accent-emerald-600"
                                        title={tDel('t_bulk_review')}
                                      />
                                    )}
                                    <FileText className="w-3 h-3 shrink-0" />
                                    {rf.category && <span className="text-[9px] font-semibold uppercase bg-emerald-200 text-emerald-800 rounded px-1 py-0.5">{rf.category}</span>}
                                    {rf.original_filename || rf.note || (rf.category === 'Other' ? 'Other incidental' : 'Bill')}
                                    {rf.note && rf.original_filename && <span className="text-emerald-600/80 italic">— {rf.note}</span>}
                                    {rf.amount != null && <span className="text-emerald-700">· ${usd2(rf.amount)}</span>}
                                    <span className={`text-[9px] font-semibold uppercase rounded px-1 py-0.5 border shrink-0 ${billTone(st)}`}>
                                      {billLabel(st)}
                                    </span>
                                  </span>
                                  <span className="flex items-center gap-2 shrink-0">
                                    {rf.original_filename && (
                                      <>
                                        <button onClick={() => setViewer({ url: `/deliverables/reimbursements/${rf.id}/download`, filename: rf.original_filename, download: () => openReimbursement(rf.id, 'attachment', rf.original_filename) })} className="text-blue-600 hover:text-blue-800 flex items-center gap-0.5"><Eye className="w-3 h-3" /> View</button>
                                        <button onClick={() => openReimbursement(rf.id, 'attachment', rf.original_filename)} className="text-gray-600 hover:text-gray-800 flex items-center gap-0.5"><Download className="w-3 h-3" /></button>
                                      </>
                                    )}
                                    {/* Per-bill decision — a reason is required to send one back. */}
                                    {canReviewBills && st === 'Pending' && (
                                      <>
                                        <button
                                          disabled={billBusy === rf.id}
                                          onClick={() => reviewBill(rf.id, 'approved')}
                                          className="px-1.5 py-0.5 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                                          title={tDel('t_approve_bill')}
                                        >{billBusy === rf.id ? '…' : '✓'}</button>
                                        <button
                                          disabled={billBusy === rf.id}
                                          onClick={() => reviewBill(rf.id, 'revision_requested')}
                                          className="px-1.5 py-0.5 rounded bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50"
                                          title={tDel('t_send_back_reason')}
                                        >↩</button>
                                        <button
                                          disabled={billBusy === rf.id}
                                          onClick={() => reviewBill(rf.id, 'rejected')}
                                          className="px-1.5 py-0.5 rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                                          title={tDel('t_reject_bill')}
                                        >✕</button>
                                      </>
                                    )}
                                    {/* Consultant answers a query on this specific bill */}
                                    {ownsMilestone(milestone) && st === 'Revision Requested' && !isArchived && !d.archived_at && !milestone.consultant_invoice_confirmed && (
                                      <button
                                        disabled={billBusy === rf.id}
                                        onClick={() => openAmend(rf)}
                                        className="px-1.5 py-0.5 rounded bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50"
                                        title={tDel('t_amend_send_back')}
                                      >{billBusy === rf.id ? '…' : '✎ Amend'}</button>
                                    )}
                                    {/* Consultant removes a bill — until the invoice is confirmed */}
                                    {isConsultant && isMyProject && !isArchived && !d.archived_at && !milestone.consultant_invoice_confirmed && (
                                      <button onClick={() => deleteBill(rf.id)} title={tDel('t_remove_bill')} className="text-gray-400 hover:text-red-600">✕</button>
                                    )}
                                  </span>
                                </div>
                                {/* The reviewer's query. Kept AFTER approval too — struck through in
                                    grey — so both sides can still see why the amount changed rather
                                    than the query vanishing the moment it is resolved. */}
                                {rf.review_comment && (
                                  <p className={`mt-0.5 ml-4 text-[11px] ${
                                    st === 'Approved' ? 'text-gray-400 line-through'
                                      : st === 'Rejected' ? 'text-red-700' : 'text-amber-800'}`}>
                                    <strong>{tDel('reviewer_asked')}</strong> {rf.review_comment}
                                    {ownsMilestone(milestone) && st === 'Revision Requested' && (
                                      <span className="text-gray-600 no-underline"> — kindly amend this bill and send it back.</span>
                                    )}
                                  </p>
                                )}
                                {rf.review_comment && st === 'Approved' && (
                                  <p className="ml-4 text-[10px] text-gray-400">{tDel('resolved_after_amendment')}</p>
                                )}
                              </div>
                            )})}
                          </div>
                          {canReviewBills && pending.length > 0 && (
                            <p className="mt-1 text-[11px] text-emerald-700/80">
                              Every bill needs a decision before the deliverable itself can be approved.
                            </p>
                          )}
                        </div>
                        )
                      })()}
                    </div>
                    {/* View (opens in browser) + Download (keeps original name) */}
                    {(d.file_storage_key || d.file_size_bytes > 0) && !d.purged_at && (
                      <div className="flex items-center gap-1.5 shrink-0">
                        <button
                          onClick={() => setViewer({ url: `/deliverables/${d.id}/download`, filename: d.original_filename, download: () => openFile(d.id, 'attachment', d.original_filename) })}
                          className="flex items-center gap-1 px-2.5 py-1 text-xs text-blue-700 border border-blue-200 rounded hover:bg-blue-50"
                        >
                          <Eye className="w-3.5 h-3.5" /> View
                        </button>
                        {canDownload && (
                          <button
                            onClick={() => openFile(d.id, 'attachment', d.original_filename)}
                            className="flex items-center gap-1 px-2.5 py-1 text-xs text-gray-700 border border-gray-200 rounded hover:bg-gray-50"
                          >
                            <Download className="w-3.5 h-3.5" /> Download
                          </button>
                        )}
                        {user?.role === userRoles.ADMIN && !isArchived && d.archived_at && !d.purged_at && (
                          <button
                            onClick={() => unarchiveDeliverable(d)}
                            title="Undo this archive (Admin): unlocks the deliverable and clears its retention clock"
                            className="flex items-center gap-1 px-2.5 py-1 text-xs text-amber-700 border border-amber-200 bg-amber-50 rounded hover:bg-amber-100"
                          >
                            <Archive className="w-3.5 h-3.5" /> Un-archive
                          </button>
                        )}
                        {isPMOrAdmin && !isArchived && !d.archived_at && d.status === 'Approved' && (
                          <button
                            onClick={() => archiveDeliverable(d)}
                            title={tDel('t_archive_deliverable')}
                            className="flex items-center gap-1 px-2.5 py-1 text-xs text-indigo-700 border border-indigo-200 bg-indigo-50 rounded hover:bg-indigo-100"
                          >
                            <Archive className="w-3.5 h-3.5" /> Archive
                          </button>
                        )}
                        {/* Whoever actually made this submission may correct it while it is not
                            yet approved: the consultant for their own, and an Admin or PM for one
                            they filed on a consultant's behalf — which is the only way to fix a
                            bill that failed to attach, since the message telling them to "Edit"
                            was pointing at a button they could not see.

                            NOT any admin on any deliverable. A consultant's own submission is
                            their declaration of what they did and what they are claiming; going
                            back into that on their behalf is a different act from correcting your
                            own typing. submitted_by_id is null unless somebody filed it for them. */}
                        {((isConsultant && isMyProject) || (d.submitted_by_id && d.submitted_by_id === user?.id))
                          && !isArchived && !d.archived_at && ['Submitted', 'Revision Requested'].includes(d.status) && (
                          <button
                            onClick={() => openEdit(d)}
                            title="Edit this deliverable (title, description, files, reimbursement bills)"
                            className="flex items-center gap-1 px-2.5 py-1 text-xs text-amber-700 border border-amber-200 rounded hover:bg-amber-50"
                          >
                            ✏️ Edit
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  {/* PM/Admin review actions — only for Submitted or Revision Requested */}
                  {isPMOrAdmin && !isArchived && !d.archived_at && ['Submitted', 'Revision Requested'].includes(d.status) && (
                    <div className="mt-3">
                      {reviewingId === d.id ? (
                        <div className="bg-gray-50 border border-gray-200 rounded p-3 space-y-2">
                          {/* AI advisory check — reads the file, recommends a decision */}
                          <AIDeliverableCheck deliverableId={d.id} />
                          <textarea
                            rows={2}
                            value={reviewNotes}
                            onChange={(e) => setReviewNotes(e.target.value)}
                            placeholder={tDel('ph_review_notes')}
                            className="w-full text-xs border border-gray-300 rounded p-2"
                          />
                          {/* Optional revision-notes attachment (used when requesting a revision) */}
                          <label className="flex items-center gap-2 text-xs text-gray-600">
                            <span className="shrink-0">{tDel('attach_revision_notes')}</span>
                            <input
                              type="file"
                              accept=".pdf,.doc,.docx,.xlsx,.pptx,.txt,.png,.jpg,.jpeg"
                              onChange={(e) => setReviewFile(e.target.files?.[0] || null)}
                              className="text-xs"
                            />
                          </label>
                          {reviewFile && <p className="text-[11px] text-gray-500">📎 {reviewFile.name} (sent when you click Request Revision)</p>}
                          <div className="flex gap-2 justify-end flex-wrap">
                            <button disabled={!!reviewBusy} onClick={() => { setReviewingId(null); setReviewNotes('') }} className="px-2.5 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded disabled:opacity-50">{tc('actions.cancel')}</button>
                            <button disabled={!!reviewBusy} onClick={() => review(d.id, 'rejected')} className="flex items-center gap-1 px-2.5 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-60">
                              {reviewBusy === 'rejected' ? <><Loader2 className="w-3 h-3 animate-spin" /> Rejecting…</> : <><X className="w-3 h-3" /> Reject</>}
                            </button>
                            <button disabled={!!reviewBusy} onClick={() => review(d.id, 'revision_requested')} className="flex items-center gap-1 px-2.5 py-1 text-xs bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-60">
                              {reviewBusy === 'revision_requested' ? <><Loader2 className="w-3 h-3 animate-spin" /> Requesting…</> : <><RefreshCw className="w-3 h-3" /> Request Revision</>}
                            </button>
                            <button disabled={!!reviewBusy} onClick={() => review(d.id, 'approved')} className="flex items-center gap-1 px-2.5 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-60">
                              {reviewBusy === 'approved' ? <><Loader2 className="w-3 h-3 animate-spin" /> Approving…</> : <><Check className="w-3 h-3" /> Approve</>}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => setReviewingId(d.id)}
                          className="px-3 py-1 text-xs font-medium text-blue-700 border border-blue-200 bg-blue-50 rounded hover:bg-blue-100"
                        >
                          Review →
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          {/* Inline milestone chat — PM, Consultant and Admin only */}
          {(isPMOrAdmin || isMyProject) && (
            <MilestoneChat
              milestoneId={milestone.id}
              projectId={project?.id}
              milestoneName={milestone.title}
              autoOpen={String(milestone.id) === String(openMilestoneChatId)}
              readOnly={isArchived}
            />
          )}
        </div>
        )
      })}

      {/* Submit deliverable modal */}
      {showSubmit && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <form onSubmit={handleFormSubmit} className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
            {/* Sticky header with a clear close button */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 shrink-0">
              <h2 className="text-lg font-bold text-gray-900">{editingDeliverable ? 'Edit Deliverable' : 'Submit a Deliverable'}</h2>
              <button type="button" onClick={closeSubmit} disabled={submitting} className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg disabled:opacity-50" aria-label="Close">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Scrollable body */}
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
            {/* Park a half-written submission and come back to it. Not offered while EDITING an
                existing deliverable: that is already saved, and a "draft" of it would be a second
                version of a live record. */}
            {!editingDeliverable && (
              <DraftBar
                kind="deliverable"
                label={form.title || (project?.name ? `Deliverable for ${project.name}` : 'Deliverable')}
                collect={collectDeliverableDraft}
                apply={applyDeliverableDraft}
                onSaved={onDeliverableDraftSaved}
                context={{ project_id: project?.id || null, milestone_id: form.milestone_id || null }}
                filesWarning
              />
            )}

            {/* Whose work this is. An admin filling this in on somebody's behalf should never be
                in any doubt about the name it will be filed under — the milestone decides it, and
                it changes when they change the milestone above. */}
            {actingAsConsultantId && (
              <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2">
                <p className="text-xs text-blue-900">
                  Submitting on behalf of <strong>{actingAsName}</strong>. The work is recorded as
                  theirs; the submission is recorded as yours, and the project manager is told both.
                </p>
              </div>
            )}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{tDel('milestone_req')}</label>
              <select
                value={form.milestone_id}
                onChange={(e) => editingDeliverable ? setForm({ ...form, milestone_id: e.target.value }) : switchSubmitMilestone(e.target.value)}
                required
                disabled={!!editingDeliverable}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm disabled:bg-gray-100 disabled:text-gray-500"
              >
                <option value="">— select —</option>
                {/* Only milestones the consultant has actually committed to (Accepted/Approved)
                    can have deliverables submitted against them — never merely Offered ones.
                    A PAID milestone stays selectable only when reimbursable (to bill the next
                    round, v3.1); a paid Fixed milestone is done, so it's hidden. */}
                {milestones
                  .filter(m => !m.scope_status || ['Accepted', 'Approved'].includes(m.scope_status))
                  .filter(m => m.status !== 'Paid' || milestoneIsReimbursable(m.id))
                  .map(m => (
                    <option key={m.id} value={m.id}>{m.title}{(m.invoice_round || 1) > 1 ? ` · Round ${m.invoice_round}` : ''}{m.payment_amount > 0 ? ` ($${usd2(m.payment_amount)})` : ''}</option>
                  ))}
              </select>
            </div>

            {!editingDeliverable && draftRestored && (
              <div className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 -mt-1">
                ✓ Draft restored — your attached files and amounts for this milestone are back. Add anything that was missing (e.g. the last bill) and submit.
              </div>
            )}
            {!editingDeliverable && !draftRestored && (
              <p className="text-[11px] text-gray-400 -mt-1">
                💾 If you close this, you'll be asked to save your progress as a draft — your title, amounts <b>and attached files</b> are kept for this milestone so you can finish later.
              </p>
            )}

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{tDel('deliverable_title_req')}</label>
              <input
                type="text"
                required
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder={tDel('ph_report_example')}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{tc('labels.description')}</label>
              <textarea
                rows={2}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder={tDel('ph_brief_notes')}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                {editingDeliverable ? 'Deliverable file' : 'Deliverable file(s) *'}
              </label>
              {editingDeliverable && (
                <div className="mb-1.5 space-y-1">
                  <div className="flex items-center justify-between text-[11px] bg-blue-50 border border-blue-200 rounded px-2 py-1">
                    <span className="truncate">★ {editingDeliverable.original_filename || 'current file'} <span className="text-gray-400">(current)</span></span>
                  </div>
                  {editExtraFiles.map(xf => (
                    <div key={xf.id} className="flex items-center justify-between text-[11px] bg-gray-50 border border-gray-200 rounded px-2 py-1">
                      <span className="truncate">{xf.original_filename}</span>
                      <button type="button" onClick={() => removeExtraFile(xf.id)} className="text-gray-400 hover:text-red-500 ml-2">✕</button>
                    </div>
                  ))}
                  <p className="text-[10px] text-gray-400">Attach a file below to REPLACE the main file (★); leave empty to keep it.</p>
                </div>
              )}
              <div {...dropZone('files')}
                className={`rounded-lg border-2 border-dashed p-2 transition-colors ${dragBucket === 'files' ? 'border-blue-400 bg-blue-50' : 'border-gray-300'}`}>
                <input
                  type="file"
                  multiple={!editingDeliverable}
                  accept=".pdf,.docx,.doc,.xlsx,.xls,.pptx,.png,.jpg,.jpeg,.gif,.txt,.csv,.zip"
                  onChange={(e) => { addFilesTo('files', e.target.files); e.target.value = '' }}
                  className="w-full text-xs"
                />
                <p className="text-[10px] text-gray-400 mt-1 text-center">or drag &amp; drop files here</p>
              </div>
              {form.files.length > 0 && (
                <ul className="mt-1.5 space-y-1">
                  {form.files.map((f, i) => (
                    <li key={i} className="flex items-center justify-between text-[11px] bg-gray-50 border border-gray-200 rounded px-2 py-1">
                      <span className="truncate">{!editingDeliverable && i === 0 ? '★ ' : ''}{editingDeliverable ? '↺ ' : ''}{f.name}</span>
                      <button type="button" onClick={() => removeFileAt('files', i)} className="text-gray-400 hover:text-red-500 ml-2">✕</button>
                    </li>
                  ))}
                </ul>
              )}
              {!editingDeliverable && <p className="text-[10px] text-gray-400 mt-1">The first file (★) is the main deliverable; add more if needed. Max 50 MB each.</p>}
            </div>

            {/* Reimbursement bills — dropdown-driven (Mam #23): pick a category, attach the
                bill(s), repeat. Shows a clear message on Fixed milestones instead of hiding. */}
            {isReimbursableProject && (
              <div className="border-t pt-3 space-y-3">
                <p className="text-xs font-semibold text-gray-700">{tDel('reimbursement_bills')} <span className="font-normal text-gray-400">(optional — helps auto-total your invoice)</span></p>

                {/* Edit mode: bills already on the deliverable, removable one by one */}
                {editingDeliverable && editBills.length > 0 && (
                  <ul className="space-y-0.5">
                    {editBills.map(rf => (
                      <li key={rf.id} className="flex items-center justify-between text-[11px] bg-emerald-50 border border-emerald-100 rounded px-2 py-1">
                        <span className="truncate flex items-center gap-1.5">
                          {rf.category && <span className="shrink-0 text-[9px] font-semibold uppercase bg-emerald-200 text-emerald-800 rounded px-1 py-0.5">{rf.category}</span>}
                          {rf.original_filename || 'Other incidental'}
                          {rf.amount != null && <span className="shrink-0 font-bold text-emerald-700">${usd2(rf.amount)}</span>}
                        </span>
                        <button type="button" onClick={() => deleteBill(rf.id)} className="text-gray-400 hover:text-red-500 ml-2">✕</button>
                      </li>
                    ))}
                  </ul>
                )}

                {!isReimbursable ? (
                  <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-2">
                    The selected milestone is <b>{tDel('fixed')}</b> — reimbursements apply to <b>{tDel('actuals')}</b> milestones only.
                    Pick an Actuals milestone above to attach your expense bills.
                  </p>
                ) : (
                  <>
                    {/* Category dropdown + attach */}
                    <div className="flex gap-2 items-stretch">
                      <select
                        value={reimbCat}
                        onChange={(e) => setReimbCat(e.target.value)}
                        className="flex-1 text-xs border border-gray-300 rounded-lg px-2 py-2 bg-white"
                      >
                        {REIMB_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                      </select>
                      <label className="shrink-0 flex items-center gap-1 px-3 py-2 text-xs font-medium text-emerald-700 border border-emerald-200 bg-emerald-50 rounded-lg cursor-pointer hover:bg-emerald-100">
                        📎 Attach bill(s)
                        <input type="file" multiple className="hidden" accept=".pdf,.docx,.doc,.xlsx,.xls,.png,.jpg,.jpeg,.gif,.zip"
                          onChange={(e) => { addFilesTo(reimbCat, e.target.files); e.target.value = '' }} />
                      </label>
                    </div>
                    {/* Drag & drop bills straight into the selected category */}
                    <div {...dropZone(reimbCat)}
                      className={`mt-2 rounded-lg border-2 border-dashed px-3 py-3 text-center text-[11px] transition-colors ${dragBucket === reimbCat ? 'border-emerald-400 bg-emerald-50 text-emerald-700' : 'border-gray-300 text-gray-400'}`}>
                      Drag &amp; drop bills here — they'll be added under <b>{reimbLabel(reimbCat)}</b>
                    </div>

                    {/* All added bills, tagged by category */}
                    {Object.values(form.reimb).some(files => files.length > 0) && (
                      <ul className="space-y-0.5">
                        {Object.entries(form.reimb).flatMap(([cat, files]) =>
                          files.map((f, i) => {
                            const pv = billPreviews[billKey(f)]
                            return (
                              <li key={`${cat}-${i}`} className="flex items-center justify-between text-[11px] bg-emerald-50 border border-emerald-100 rounded px-2 py-1">
                                <span className="truncate flex items-center gap-1.5">
                                  <span className="shrink-0 text-[9px] font-semibold uppercase bg-emerald-200 text-emerald-800 rounded px-1 py-0.5">{reimbLabel(cat)}</span>
                                  {f.name}
                                  {/* Extracted amount preview — verify before submitting */}
                                  {pv?.status === 'loading' && <span className="shrink-0 text-gray-400 italic">reading amount…</span>}
                                  {pv?.status === 'done' && pv.amount_usd != null && (
                                    <span className="shrink-0 font-bold text-emerald-700">
                                      ${usd2(pv.amount_usd)}
                                      {pv.currency && pv.currency !== 'USD' && pv.amount != null && (
                                        <span className="font-normal text-emerald-600"> ({pv.currency} {Number(pv.amount).toLocaleString()})</span>
                                      )}
                                    </span>
                                  )}
                                  {((pv?.status === 'done' && pv.amount_usd == null) || pv?.status === 'failed') && (
                                    <span className="shrink-0 text-amber-600">⚠ amount not read — will need manual check</span>
                                  )}
                                </span>
                                <button type="button" onClick={() => removeFileAt(cat, i)} className="text-gray-400 hover:text-red-500 ml-2">✕</button>
                              </li>
                            )
                          })
                        )}
                      </ul>
                    )}
                    {/* Running total of what the AI read + the typed Other amount — the figure
                        the invoice will show, visible BEFORE submitting. */}
                    {(Object.values(form.reimb).some(files => files.length > 0) || parseFloat(form.otherAmount) > 0) && (() => {
                      const billSum = Object.values(form.reimb).flat()
                        .reduce((s, f) => s + (Number(billPreviews[billKey(f)]?.amount_usd) || 0), 0)
                      const other = parseFloat(form.otherAmount) || 0
                      const anyPending = Object.values(form.reimb).flat().some(f => billPreviews[billKey(f)]?.status === 'loading')
                      return (
                        <p className="text-[11px] text-emerald-800 font-semibold">
                          Extracted claim total: ${usd2(billSum + other)}
                          {anyPending && <span className="font-normal text-gray-400"> (still reading…)</span>}
                          <span className="font-normal text-gray-500"> — check this is right before you submit.</span>
                        </p>
                      )
                    })()}

                    {/* Incidentals — Other without a bill: amount + note */}
                    <div>
                      <label className="block text-[11px] text-gray-600 mb-1">{tDel('incidentals_other')}</label>
                      <div className="flex gap-2">
                        <input type="number" min="0" step="0.01" value={form.otherAmount}
                          onChange={(e) => setForm({ ...form, otherAmount: e.target.value })}
                          placeholder={tDel('ph_amount')}
                          className="w-32 text-xs border border-gray-300 rounded-lg px-2 py-1.5 bg-white" />
                        <input type="text" value={form.otherNote}
                          onChange={(e) => setForm({ ...form, otherNote: e.target.value })}
                          placeholder={parseFloat(form.otherAmount) > 0 ? 'What was this amount for? (required)' : 'Note'}
                          className={`flex-1 text-xs border rounded-lg px-2 py-1.5 bg-white ${parseFloat(form.otherAmount) > 0 && !form.otherNote.trim() ? 'border-red-400' : 'border-gray-300'}`} />
                      </div>
                    </div>
                    <p className="text-[10px] text-gray-400">Finance &amp; PM see these with your invoice; the AI totals each category.</p>
                  </>
                )}
              </div>
            )}

            </div>

            {/* Sticky footer actions */}
            <div className="flex items-center gap-2 px-6 py-3 border-t border-gray-200 bg-gray-50 shrink-0">
              {editingDeliverable && (
                <button type="button" onClick={withdrawDeliverable} disabled={submitting} className="px-3 py-2 text-xs text-red-600 border border-red-200 rounded-lg hover:bg-red-50 mr-auto">
                  🗑 Delete deliverable
                </button>
              )}
              <button type="button" onClick={closeSubmit} disabled={submitting} className={`px-4 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 ${editingDeliverable ? '' : 'ml-auto'}`}>
                Cancel
              </button>
              {(() => {
                const hasClaims = milestoneIsReimbursable(form.milestone_id) && (
                  Object.values(form.reimb).some(files => files.length > 0) ||
                  (parseFloat(form.otherAmount) > 0) ||
                  (editingDeliverable && editBills.length > 0)
                )
                const label = editingDeliverable
                  ? (hasClaims ? 'Verify & Save' : 'Save & Resubmit')
                  : (hasClaims ? 'Verify & Submit' : 'Submit')
                return (
                  <button type="submit" disabled={submitting} className="flex items-center gap-1.5 px-5 py-2 text-sm bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50">
                    {submitting
                      ? <><Loader2 className="w-4 h-4 animate-spin" /> {editingDeliverable ? 'Saving…' : 'Submitting…'}</>
                      : <><Upload className="w-4 h-4" /> {label}</>}
                  </button>
                )
              })()}
            </div>
          </form>
        </div>
      )}

      {/* Verify & Submit — the claim table the consultant confirms before the deliverable
          actually submits. Every bill's extracted + live-USD amount is shown; nothing is
          claimed blind. Confirm → submit/save proceeds; Go back → return to the form. */}
      {verifyOpen && (() => {
        const rows = buildVerifyRows()
        const total = rows.reduce((s, r) => s + (r.usd || 0), 0)
        const anyPending = rows.some(r => r.status === 'loading')
        const anyUnread = rows.some(r => r.status !== 'loading' && r.usd == null)
        return (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-xl w-full max-h-[85vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
              <div className="px-6 py-4 border-b border-gray-200 shrink-0">
                <h2 className="text-lg font-bold text-gray-900">{tDel('verify_claim')}</h2>
                <p className="text-xs text-gray-500 mt-0.5">The AI read these from your bills and converted them at each bill's date. Check them — you can fix the amount, currency or date and the USD updates automatically. This is what your invoice will claim.</p>
              </div>
              <div className="flex-1 overflow-y-auto px-6 py-4">
                {rows.map(r => r.editable ? (
                  <div key={r.key} className="border border-gray-200 rounded-lg p-3 mb-2">
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <span className="text-xs font-medium text-gray-800 truncate max-w-[220px]">{r.name}</span>
                      <span className="text-[10px] font-semibold uppercase bg-emerald-100 text-emerald-800 rounded px-1.5 py-0.5 shrink-0">{r.cat}</span>
                    </div>
                    {r.status === 'loading' ? (
                      <p className="text-xs text-gray-400 italic">reading amount…</p>
                    ) : (
                      <>
                        <div className="flex flex-wrap gap-2 items-end">
                          <label className="text-[10px] text-gray-500 flex-1 min-w-[90px]">Amount
                            <input type="number" step="0.01" min="0" value={r.amount ?? ''}
                              onChange={e => updateBill(r.file, { amount: e.target.value })}
                              className="mt-0.5 w-full px-2 py-1 text-sm border border-gray-300 rounded" placeholder={tDel('ph_zero')} />
                          </label>
                          <label className="text-[10px] text-gray-500">Currency
                            <input type="text" value={r.currency ?? ''} maxLength={4}
                              onChange={e => updateBill(r.file, { currency: e.target.value.toUpperCase() })}
                              className="mt-0.5 w-16 px-2 py-1 text-sm border border-gray-300 rounded uppercase" placeholder="USD" />
                          </label>
                          <label className="text-[10px] text-gray-500">Bill date
                            <input type="date" value={/^\d{4}-\d{2}-\d{2}$/.test(r.date || '') ? r.date : ''}
                              onChange={e => updateBill(r.file, { date: e.target.value })}
                              className="mt-0.5 px-2 py-1 text-sm border border-gray-300 rounded" />
                          </label>
                        </div>
                        <div className="text-right mt-1.5">
                          {r.usd != null
                            ? <span className="text-sm font-semibold text-emerald-700">${r.usd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            : <span className="text-sm text-amber-600">enter the amount ⚠</span>}
                          {r.rateDate && <span className="block text-[10px] text-gray-400">converted at the rate on {r.rateDate}</span>}
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  <div key={r.key} className="flex items-center justify-between gap-2 border-b border-gray-100 py-2">
                    <div className="min-w-0">
                      <p className="text-sm text-gray-800 truncate max-w-[220px]">{r.name}</p>
                      <span className="text-[10px] font-semibold uppercase bg-emerald-100 text-emerald-800 rounded px-1.5 py-0.5">{r.cat}</span>
                    </div>
                    <div className="text-right shrink-0">
                      {r.orig && <span className="block text-[11px] text-gray-500">{r.orig}</span>}
                      {r.usd != null
                        ? <span className="text-sm font-semibold text-emerald-700">${r.usd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        : <span className="text-sm text-amber-600">not read ⚠</span>}
                    </div>
                  </div>
                ))}
                <div className="flex items-center justify-between mt-3 px-3 py-2 rounded-lg bg-emerald-50 border border-emerald-200">
                  <span className="font-bold text-gray-900">{tDel('claim_total')}</span>
                  <span className="font-bold text-emerald-700">${total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
                {anyUnread && (
                  <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-3">
                    One or more bills has no amount yet. Type the amount (and currency/date) above so it isn't claimed as $0.
                  </p>
                )}
              </div>
              <div className="flex justify-end gap-2 px-6 py-3 border-t border-gray-200 bg-gray-50 shrink-0">
                <button type="button" onClick={() => setVerifyOpen(false)} disabled={submitting} className="px-4 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
                  ← Go back &amp; edit
                </button>
                <button
                  type="button"
                  disabled={submitting || anyPending}
                  onClick={async () => { setVerifyOpen(false); editingDeliverable ? await saveEdit() : await doSubmit() }}
                  className="flex items-center gap-1.5 px-5 py-2 text-sm bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-50"
                >
                  {anyPending ? 'Reading amounts…' : '✓ Confirm & Submit'}
                </button>
              </div>
            </div>
          </div>
        )
      })()}

      {/* Confirm Invoice modal — review bank details + claim amount, then sign */}
      {confirmInvoiceModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[85vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-200 shrink-0">
              <h2 className="text-lg font-bold text-gray-900">{tDel('confirm_sign_invoice')}</h2>
              <p className="text-xs text-gray-500 mt-0.5">Review the details below, then sign to confirm this invoice is correct.</p>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              <div>
                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">{tDel('payment_sent_to')}</p>
                {confirmInvoiceModal.banking ? (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 text-sm space-y-0.5">
                    {confirmInvoiceModal.banking.account_holder_name && <p>A/C name: <span className="font-medium">{confirmInvoiceModal.banking.account_holder_name}</span></p>}
                    {confirmInvoiceModal.banking.bank_name && <p>{tDel('bank_label')} <span className="font-medium">{confirmInvoiceModal.banking.bank_name}</span></p>}
                    {confirmInvoiceModal.banking.account_number && <p>A/C no: <span className="font-medium">{confirmInvoiceModal.banking.account_number}</span></p>}
                    {confirmInvoiceModal.banking.iban && <p>{tDel('iban_label')} <span className="font-medium">{confirmInvoiceModal.banking.iban}</span></p>}
                    {confirmInvoiceModal.banking.swift_code && <p>{tDel('swift_label')} <span className="font-medium">{confirmInvoiceModal.banking.swift_code}</span></p>}
                    {confirmInvoiceModal.banking.currency && <p>{tDel('currency_label')} <span className="font-medium">{confirmInvoiceModal.banking.currency}</span></p>}
                  </div>
                ) : (
                  <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5">
                    No bank details on file. You'll be asked to add them after signing.
                  </p>
                )}
                <button
                  type="button"
                  onClick={() => { const mid = confirmInvoiceModal?.milestoneId; setConfirmInvoiceModal(null); navigate(`/profile-settings?edit=banking&return=${encodeURIComponent(`/projects/${project?.id}?tab=deliverables&confirmInvoice=${mid}`)}`) }}
                  className="text-[11px] text-blue-600 hover:underline mt-1"
                >
                  Not correct? Update bank details
                </button>
              </div>

              {confirmInvoiceModal.reimbursable && (
                <div>
                  <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">{tDel('amount_claimed')}</p>
                  {confirmInvoiceModal.reimbTotal > 0 ? (
                    <p className="bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2.5 text-sm">
                      Your attached bills total <span className="font-bold text-emerald-700">${confirmInvoiceModal.reimbTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </p>
                  ) : (
                    <>
                      <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-2">
                        {confirmInvoiceModal.reimbCount > 0
                          ? `You have ${confirmInvoiceModal.reimbCount} bill(s) attached, but the amount couldn't be read automatically — please confirm or enter the total you're claiming.`
                          : `No reimbursement bills found — enter the ${confirmInvoiceModal.budgetType === 'Actual' ? 'actual amount you are claiming' : 'actual expenses to claim on top of the fixed fee'}.`}
                      </p>
                      <input
                        type="number" min="0" step="0.01"
                        value={confirmClaimAmount}
                        onChange={e => setConfirmClaimAmount(e.target.value)}
                        placeholder={tDel('ph_amount')}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      />
                    </>
                  )}
                </div>
              )}

              <div>
                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">{tDel('your_signature')}</p>
                <SignaturePad
                  name={confirmSigName}
                  onNameChange={setConfirmSigName}
                  signatureDataUrl={confirmSigData}
                  onSignatureChange={setConfirmSigData}
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 px-6 py-3 border-t border-gray-200 bg-gray-50 shrink-0">
              <button type="button" onClick={() => setConfirmInvoiceModal(null)} disabled={confirmSubmitting} className="px-4 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
                Cancel
              </button>
              <button
                type="button"
                disabled={confirmSubmitting || !confirmSigName.trim()}
                onClick={submitInvoiceConfirmation}
                className="flex items-center gap-1.5 px-5 py-2 text-sm bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-50"
              >
                {confirmSubmitting ? 'Confirming…' : '✓ Confirm & Sign Invoice'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invoice View Modal */}
      {viewInvoiceMilestoneId && (
        <InvoiceView
          milestoneId={viewInvoiceMilestoneId}
          onClose={() => setViewInvoiceMilestoneId(null)}
        />
      )}

      {/* Amend a queried bill — the consultant's reply to a reviewer's request. */}
      {amendingBill && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setAmendingBill(null)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-5 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <h3 className="text-base font-semibold text-gray-900">{tDel('amend_this_bill')}</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {amendingBill.original_filename || amendingBill.note || 'Bill'}
            </p>
            <div className="mt-3 text-xs bg-amber-50 border border-amber-200 rounded p-2 text-amber-900">
              <strong>{tDel('reviewer_asked')}</strong> {amendingBill.review_comment || '—'}
            </div>

            <label className="block text-xs font-medium text-gray-600 mt-4 mb-1">
              Replace the receipt <span className="text-gray-400">(optional — leave empty to keep the current file)</span>
            </label>
            {/* The browser's default file input renders as unstyled grey text, which read as a
                dead label rather than something to click. Hidden input + styled label instead. */}
            <div className="flex items-center gap-2 flex-wrap">
              <label className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-blue-700 bg-blue-50 border border-blue-200 rounded-lg cursor-pointer hover:bg-blue-100 transition-colors">
                <Upload className="w-3.5 h-3.5" />
                {amendFile ? 'Choose a different file' : 'Choose file'}
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={e => scanAmendFile(e.target.files?.[0] || null)}
                  className="hidden"
                />
              </label>
              {amendFile ? (
                <span className="inline-flex items-center gap-1.5 text-xs text-gray-700 bg-gray-100 border border-gray-200 rounded-lg px-2 py-1.5 max-w-[55%]">
                  <FileText className="w-3 h-3 shrink-0 text-gray-500" />
                  <span className="truncate" title={amendFile.name}>{amendFile.name}</span>
                  <button
                    type="button"
                    onClick={() => { setAmendFile(null); setAmendScan(null) }}
                    title={tDel('t_remove_file')}
                    className="text-gray-400 hover:text-red-600 shrink-0"
                  >✕</button>
                </span>
              ) : (
                <span className="text-xs text-gray-400">{tDel('no_new_file')}</span>
              )}
            </div>

            {/* AI read of the replacement receipt — same extractor as the submit flow, but for
                this one bill. The consultant confirms/corrects before it goes back. */}
            {amendScan === 'loading' && (
              <p className="mt-2 text-xs text-blue-700 flex items-center gap-1.5">
                <Loader2 className="w-3 h-3 animate-spin" /> Reading the receipt…
              </p>
            )}
            {amendScan === 'failed' && (
              <p className="mt-2 text-xs text-amber-700">
                Couldn't read that receipt automatically — please type the amount yourself below.
              </p>
            )}

            {(amendFile || amendCur) && (
              <div className="mt-3 border border-blue-200 bg-blue-50 rounded-lg p-3">
                <p className="text-[11px] font-semibold text-blue-900 mb-2">
                  {amendScan === 'done' ? 'Check what we read from the receipt' : 'Bill details'}
                </p>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="block text-[10px] text-gray-600 mb-0.5">{tDel('amount_on_bill')}</label>
                    <input
                      type="number" step="0.01" min="0"
                      value={amendOrig}
                      onChange={e => { setAmendOrig(e.target.value); reconvertAmend({ orig: e.target.value }) }}
                      className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] text-gray-600 mb-0.5">{tc('labels.currency')}</label>
                    <input
                      type="text" maxLength={5}
                      value={amendCur}
                      onChange={e => { const v = e.target.value.toUpperCase(); setAmendCur(v); reconvertAmend({ cur: v }) }}
                      className="w-full border border-gray-300 rounded px-2 py-1 text-xs uppercase"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] text-gray-600 mb-0.5">{tDel('date_on_bill')}</label>
                    <input
                      type="date"
                      value={amendDate}
                      onChange={e => { setAmendDate(e.target.value); reconvertAmend({ date: e.target.value }) }}
                      className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                    />
                  </div>
                </div>
                <p className="text-[10px] text-blue-800 mt-2">
                  Converted using the exchange rate for the bill's date. Correct anything above and the
                  USD figure updates.
                </p>
              </div>
            )}

            <label className="block text-xs font-medium text-gray-600 mt-3 mb-1">
              Amount claimed (USD) <span className="text-gray-400">— this is what the invoice uses</span>
            </label>
            <input
              type="number" step="0.01" min="0"
              value={amendAmount}
              onChange={e => setAmendAmount(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-semibold"
            />

            <label className="block text-xs font-medium text-gray-600 mt-3 mb-1">
              Description / justification
            </label>
            <textarea
              rows={3}
              value={amendNote}
              onChange={e => setAmendNote(e.target.value)}
              placeholder={tDel('ph_justify_expense')}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />

            <div className="flex justify-end gap-2 mt-5">
              <button
                onClick={() => setAmendingBill(null)}
                className="px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >{tc('actions.cancel')}</button>
              <button
                disabled={billBusy === amendingBill.id}
                onClick={submitAmend}
                className="px-3 py-1.5 text-sm font-semibold bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50"
              >{billBusy === amendingBill.id ? 'Sending…' : 'Send back for review'}</button>
            </div>
          </div>
        </div>
      )}

      {/* In-app file preview (View) */}
      <FileViewerModal
        open={!!viewer}
        onClose={() => setViewer(null)}
        fetchUrl={viewer?.url}
        filename={viewer?.filename}
        onDownload={viewer?.download}
      />
    </div>
  )
}
